<script setup>
import { getInboxLabel } from 'dashboard/helper/inboxLabel';
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useFunctionGetter, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import IntegrationsAPI from 'dashboard/api/integrations';
import BankIntegrationPicker from './BankIntegrationPicker.vue';
import BankLogo from './BankLogo.vue';
import { BANK_OPTIONS, BANK_CARD_META } from './bankCatalog';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  buildIxcFinancialTrustUnlockSettings,
  normalizeIxcApiKey,
  normalizeIxcApiUrl,
} from './helpers/ixcSettings';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
    validator: value => ['banking', 'corban', 'erp'].includes(value),
  },
});

const providerLogos = {
  ixcsoft: '/brand-assets/integrations/ixcsoft.webp',
  mkauth: '/brand-assets/integrations/mkauth.png',
};

const { isAdmin } = useAdmin();

const BANKS_WITH_CREDENTIALS = [
  'anchor',
  'bank321',
  'c6',
  'facta',
  'presenca_bank',
  'v8',
  'soma',
  'bull',
  'neon',
  'bmg',
  'fintech_corban',
];
const CLIENT_CREDENTIAL_BANKS = ['soma', 'bull', 'anchor'];
const BANK_PROVIDER_ALIASES = {
  presenca_bank: ['presenca_bank', 'prata'],
};

const providerAliases = provider => [
  provider,
  ...(BANK_PROVIDER_ALIASES[provider] || []),
];

const providerMatches = (expectedProvider, actualProvider) =>
  providerAliases(expectedProvider).includes(actualProvider);

const BANK_PRODUCT_OPTIONS = [
  { value: 'FGTS', label: 'FGTS' },
  { value: 'CLT', label: 'CLT' },
];

const FINTECH_CORBAN_BASE_URLS = {
  production: 'https://api.fintechdocorban.com.br/super-simples',
  staging: 'https://api.hml.fintechdocorban.com.br/super-simples',
};
const NEON_BASE_URLS = {
  production: 'https://partner.neon.com.br/sales-portal-bff',
  staging: 'https://partner-hml.neon.com.br/sales-portal-bff',
};
const SOMA_WEBHOOK_URL = 'https://openai.talkvia.com.br/webhook/soma';

const FACTA_PRODUCTS_WITH_TABLE = [];

const ERP_OPTIONS = [
  { value: 'ixcsoft', label: 'IXCsoft' },
  { value: 'mkauth', label: 'MkAuth' },
];

const CORBAN_OPTIONS = [
  { value: 'vanguard', label: 'Vanguard' },
  { value: 'multicorban', label: 'MultiCorban' },
];

const DEFAULT_CORBAN_HEADER_BY_PROVIDER = {
  vanguard: 'vanguard-access-token',
  multicorban: 'Authorization',
};

const CORBAN_PRODUCT_OPTIONS = [
  { value: 'SIAPE', label: 'SIAPE' },
  { value: 'INSS', label: 'INSS (CPF)' },
  { value: 'INSS_MATRICULA', label: 'INSS (Matrícula)' },
  { value: 'CLT_FGTS', label: 'CLT/FGTS' },
];

const CORBAN_HTTP_METHOD_OPTIONS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
];

const CORBAN_MAPPING_FIELDS = [
  { key: 'name', label: 'Nome', placeholder: 'dados.nome' },
  {
    key: 'birth_date',
    label: 'Data de nascimento',
    placeholder: 'dados.data_nascimento',
  },
  { key: 'mother_name', label: 'Nome da mãe', placeholder: 'dados.nome_mae' },
  {
    key: 'available_margin',
    label: 'Margem disponível',
    placeholder: 'dados.margem_disponivel',
  },
];

const emptyRequestHeader = provider => ({
  key: DEFAULT_CORBAN_HEADER_BY_PROVIDER[provider] || '',
  value: '',
});

const normalizeCorbanHeaders = headers =>
  (Array.isArray(headers) ? headers : [])
    .map(header => ({
      key: String(header?.key || '').trim(),
      value: String(header?.value || '').trim(),
    }))
    .filter(header => header.key || header.value);

const extractLegacyCorbanHeader = (hookSettings = {}, provider = '') => {
  const headerName =
    hookSettings.authorization_header_name ||
    DEFAULT_CORBAN_HEADER_BY_PROVIDER[provider] ||
    '';
  const headerValue = hookSettings.authorization_header_value || '';

  if (!headerName && !headerValue) return [];

  return [
    {
      key: headerName,
      value: headerValue.replace('{{apikey}}', hookSettings.api_key || ''),
    },
  ];
};

const resolveCorbanHeaders = (hookSettings = {}, provider = '') => {
  const normalized = normalizeCorbanHeaders(hookSettings.headers);
  if (normalized.length) return normalized;

  const legacy = extractLegacyCorbanHeader(hookSettings, provider);
  if (legacy.length) return legacy;

  return [emptyRequestHeader(provider)];
};

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const uiFlags = useMapGetter('integrations/getUIFlags');
const integration = useFunctionGetter(
  'integrations/getIntegration',
  props.integrationId
);
const providerDialogRef = ref(null);
const mappingReviewDialogRef = ref(null);
const isValidatingConnection = ref(false);
const isRegisteringSomaWebhook = ref(false);
const isConsultingFactaTables = ref(false);
const isConsultingC6TypistCode = ref(false);
const localCopy = {
  delete: 'Excluir configuração',
  deleteSuccess: 'Configuração excluída com sucesso.',
  deleteError: 'Não foi possível excluir a configuração.',
  activeStatus: 'Integração ativa',
  inactiveStatus: 'Integração inativa',
  activeAction: 'Clique para inativar',
  inactiveAction: 'Clique para ativar',
  validationSuccess: 'Conexão validada com sucesso.',
  validationError: 'Não foi possível validar a conexão.',
  validationRequiredToEnable:
    'Valide a conexão bancária antes de ativar esta integração.',
  somaWebhookTitle: 'Atualizações automáticas',
  somaWebhookDescription:
    'Cadastre este endereço na Soma para receber mudanças de consultas e propostas.',
  somaWebhookRegister: 'Cadastrar webhook',
  somaWebhookRegistered: 'Webhook cadastrado',
  somaWebhookRegisteredAt: 'Cadastrado em',
  somaWebhookSaveFirst: 'Salve a configuração para cadastrar o webhook.',
  somaWebhookSuccess: 'Webhook da Soma cadastrado com sucesso.',
  factaTableLabel: 'Código da tabela (Facta)',
  factaTableSearchPlaceholder: 'Digite código ou nome da tabela',
  factaTableEmpty: 'Nenhuma tabela encontrada para o filtro informado.',
  factaTablePlaceholder: 'Selecione ou digite o código da tabela',
  factaTableHint:
    'A tabela é carregada na validação da conexão e será salva na integração.',
  factaTableProducts: 'Produtos com tabela obrigatória',
  factaTableConsult: 'Consultar tabelas',
  factaTableAllTables: 'Todas as tabelas (maior comissão)',
  factaTableConsultSuccess: 'Tabelas Facta atualizadas com sucesso.',
  c6TypistCodeConsult: 'Consultar código',
  c6TypistCodeSuccess: 'Código do C6 consultado com sucesso.',
  c6TypistCodeCredentialsRequired:
    'Informe o usuário e a senha do C6 para consultar o código.',
  c6TypistCodeTimeout:
    'A consulta demorou mais de 120 segundos. Tente novamente em alguns instantes.',
  factaTableConsultPartial:
    'Alguns produtos não retornaram tabelas. Confira as credenciais e tente novamente.',
  factaTableRequiredProductsHint:
    'Para Facta, apenas INSS exige seleção de tabela. CLT não exige tabela.',
  factaTableSelectProductHint: 'Selecione o produto para escolher a tabela.',
  factaTableRequiredError: 'Selecione uma tabela para o produto INSS.',
  productConfig: 'Configuração de produto e banco',
  productConfigDescription:
    'Defina a prioridade de consulta dos bancos por produto.',
  productOrderLabel: 'Ordem de consulta',
  productOrderDragHint:
    'Segure e arraste para mudar a prioridade. As setas continuam disponíveis para ajuste fino.',
  offerSending: 'Envio de oferta',
  offerSendingDescription:
    'Enviar uma nova oferta quando o cliente ficar sem resposta após a simulação.',
  offerDelay: 'Aguardar',
  offerMode: 'Escolha da tabela',
  offerModeAuto: 'Automática - melhor condição para o cliente',
  offerModeManual: 'Manual - tabela selecionada pelo administrador',
  offerTable: 'Tabela manual',
  offerTablePlaceholder: 'Código ou nome da tabela',
  offerMessage: 'Mensagem da oferta',
  offerMessagePlaceholder:
    'Encontrei uma condição melhor para você. Posso te enviar a oferta?',
  saveProductConfig: 'Salvar configuração',
  saveProductConfigSuccess: 'Configuração de produto salva com sucesso.',
  saveProductConfigError: 'Não foi possível salvar a configuração de produto.',
  followupAuto: 'Follow-up automático',
  followupDescription: 'Enviar mensagem após inatividade do cliente',
  followupDelay: 'Aguardar',
  followupMessage: 'Mensagem',
  followupPlaceholder:
    'Olá! Sua proposta ainda está disponível. Posso te ajudar?',
  followupItem: 'Follow-up',
  followupAdd: 'Adicionar follow-up',
  followupEmpty: 'Nenhum follow-up configurado.',
  followupUnitMinutes: 'Minutos',
  followupUnitHours: 'Horas',
  followupUnitDays: 'Dias',
  mappingReviewTitle: 'Revisar de/para dos campos',
  mappingReviewDescription:
    'Confira os caminhos sugeridos com base no retorno da API. Ajuste antes de salvar se algum campo estiver errado.',
  mappingDetected: 'Campos sugeridos pela API.',
  mappingNotDetected:
    'Não encontramos todos os campos automaticamente. Preencha ou ajuste manualmente.',
  detectedPaths: 'Caminhos encontrados no JSON',
  httpStatus: 'HTTP',
  applyMapping: 'Aplicar mapeamento',
  closeMapping: 'Fechar',
  connectionReason: 'Motivo',
  product: 'Produto',
  productsHint:
    'Selecione um ou mais produtos disponíveis para esta integração.',
  productPlaceholder: 'Selecione o produto',
  products: 'Produtos da integração',
  productUrl: 'URL do produto',
  requestHeaders: 'Headers da requisição',
  requestHeadersHelp:
    'Configure os headers exatamente como sua API espera receber.',
  customAuthorizationHeader: 'Nome do header',
  customAuthorizationValue: 'Valor do header',
  addHeader: 'Adicionar header',
  addHeaderLabel: '+ Adicionar header',
  removeHeader: 'Remover header',
  requestMethod: 'Método',
  requestBody: 'Body JSON',
  requestBodyHelp:
    'Cole aqui o JSON enviado no Postman quando o método for POST. Você pode usar {cpf}, {matricula}, {identifier} e {identifier_type} para substituição dinâmica.',
  requestBodyPlaceholder: '{\n  "beneficio": 2285003638\n}',
  fieldMapping: 'Mapeamento do retorno JSON',
  fieldMappingHelp:
    'Informe o caminho do campo recebido no JSON. Ex.: cliente.nome ou data.nome. URL e Body aceitam {cpf}, {matricula}, {identifier} e {identifier_type}.',
  corbanValidationRequired:
    'Selecione ao menos um produto Corban e preencha a URL do produto ativo.',
  corbanHeadersRequired:
    'Adicione ao menos um header com nome e valor para validar a conexão.',
  invoiceNotifications: 'Avisos de boleto em aberto',
  invoiceNotificationsDescription:
    'Configure disparos automáticos para clientes com boletos em aberto no IXCsoft.',
  invoiceNotificationsEnabled: 'Ativar avisos automáticos',
  invoiceStartTime: 'Iniciar disparos às',
  invoiceInterval: 'Intervalo entre mensagens (segundos)',
  invoiceDueDays: 'Dias de vencimento dos boletos',
  invoiceDueDaysHelp: 'Selecione os dias do mês em que o processo deve rodar.',
  invoiceBeforeDue: 'Antes do vencimento',
  invoiceAfterDue: 'Após o vencimento',
  invoiceDaysBefore: 'Dias antes do vencimento',
  invoiceDaysAfter: 'Dias após o vencimento',
  invoiceInbox: 'Caixa de entrada',
  invoiceInboxPlaceholder: 'Selecione a caixa de entrada',
  invoiceTemplate: 'Template',
  invoiceTemplatePlaceholder: 'Selecione o template',
  invoiceNoTemplates:
    'Selecione uma caixa com templates sincronizados para escolher o envio.',
  invoiceReport: 'Relatório de envios',
  customerServiceOrder: 'Abertura OS Cliente',
  customerServiceOrderDescription:
    'Permite a abertura de ordem de serviço para clientes pelo provedor integrado.',
  customerServiceOrderEnabled: 'Permitir abertura de OS',
  customerServiceOrderActivate: 'Ativar abertura de OS',
  customerServiceOrderDeactivate: 'Desativar abertura de OS',
  customerServiceOpening: 'Abertura de atendimento cliente',
  customerServiceOpeningDescription:
    'Permite a abertura de atendimento para clientes pelo IXCsoft.',
  customerServiceOpeningEnabled: 'Permitir abertura de atendimento',
  customerServiceOpeningActivate: 'Ativar abertura de atendimento',
  customerServiceOpeningDeactivate: 'Desativar abertura de atendimento',
  financialTrustUnlock: 'Liberação de acesso por confiança',
  financialTrustUnlockDescription:
    'Permite ao IXCsoft liberar o acesso financeiro do cliente pela regra de confiança.',
  financialTrustUnlockActivate: 'Ativar liberação por confiança',
  financialTrustUnlockDeactivate: 'Desativar liberação por confiança',
  ixcApiUrlInvalid:
    'Informe uma URL válida com http ou https. Caminhos como /admin.php serão removidos.',
  ixcApiKeyHint:
    'Informe a chave em Base64 ou use o formato usuário:chave para converter automaticamente.',
  mkauthApiUrlInvalid: 'A URL do MkAuth deve ser válida e usar HTTPS.',
};

const emptyInvoiceRule = () => ({
  enabled: false,
  days: 0,
  inbox_id: null,
  template_name: '',
  template_language: '',
});

const emptyInvoiceNotifications = () => ({
  enabled: false,
  start_time: '08:00',
  due_days: [],
  message_interval_seconds: 30,
  before_due: emptyInvoiceRule(),
  after_due: emptyInvoiceRule(),
});

const emptyCustomerServiceOrder = () => ({
  enabled: false,
});

const emptyCustomerServiceOpening = () => ({
  enabled: false,
});

const emptyFinancialTrustUnlock = () =>
  buildIxcFinancialTrustUnlockSettings().financial_trust_unlock;

const delayUnitOptions = computed(() => [
  { value: 'minutes', label: localCopy.followupUnitMinutes },
  { value: 'hours', label: localCopy.followupUnitHours },
  { value: 'days', label: localCopy.followupUnitDays },
]);

const offerModeOptions = computed(() => [
  { value: 'auto', label: localCopy.offerModeAuto },
  { value: 'manual', label: localCopy.offerModeManual },
]);

const settings = reactive({
  enabled: false,
  provider: '',
  productsSelected: [],
  factaTableCode: '',
  factaTables: [],
  factaTableCodesByProduct: {
    FGTS: '',
    INSS: '',
  },
  factaTablesByProduct: {
    FGTS: [],
    INSS: [],
  },
  factaActiveProductTab: 'FGTS',
  username: '',
  password: '',
  clientId: '',
  clientSecret: '',
  somaConsultaUnificada: false,
  somaSemSeguro: false,
  bullEnvironment: 'production',
  neonOrigin: '',
  neonTokenId: '',
  neonValidationCpf: '',
  typistCpf: '',
  masterIndicator: '',
  corbanHeaderName: '',
  apiKey: '',
  headers: [],
  apiUrl: '',
  products: {},
  customerServiceOrder: emptyCustomerServiceOrder(),
  customerServiceOpening: emptyCustomerServiceOpening(),
  financialTrustUnlock: emptyFinancialTrustUnlock(),
  invoiceNotifications: emptyInvoiceNotifications(),
});

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAccountFeature = feature =>
  isFeatureEnabledonAccount.value(accountId.value, feature);

const erpProviderOptions = computed(() =>
  ERP_OPTIONS.filter(option => {
    if (option.value === 'ixcsoft') {
      return hasAccountFeature(FEATURE_FLAGS.TALKVIA_PROVIDER_IXC);
    }

    if (option.value === 'mkauth') {
      return hasAccountFeature(FEATURE_FLAGS.TALKVIA_PROVIDER_MKAUTH);
    }

    return false;
  })
);

const corbanProviderOptions = computed(() =>
  CORBAN_OPTIONS.filter(option => {
    const hasCombinedModule = hasAccountFeature(
      FEATURE_FLAGS.TALKVIA_CORBAN_MULTICORBAN_VANGUARD
    );

    if (option.value === 'multicorban') {
      return (
        hasCombinedModule ||
        hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_INSS_MULTICORBAN)
      );
    }

    if (option.value === 'vanguard') {
      return (
        hasCombinedModule ||
        hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_INSS_VANGUARD)
      );
    }

    return false;
  })
);

const bankValidationState = reactive({
  provider: '',
  valid: false,
});

const mappingReview = reactive({
  products: {},
});

const emptyOfferSending = () => ({
  enabled: false,
  delay_value: 60,
  delay_unit: 'minutes',
  mode: 'auto',
  table_code: '',
  message: '',
});

const bankConfig = reactive({
  crossSell: { enabled: false, primary: 'CLT' },
  productOrder: { FGTS: [], CLT: [] },
  inboxProductOrder: {},
  priorityFlags: [],
  productFollowup: {
    FGTS: { enabled: false, items: [] },
    CLT: { enabled: false, items: [] },
  },
  offerSending: {
    FGTS: emptyOfferSending(),
    CLT: emptyOfferSending(),
  },
});

const draggedBank = ref(null);
const bankStatusDialogRef = ref(null);
const pendingBankStatus = ref(null);
const isUpdatingBankStatus = ref(false);
const activeBankProductTab = ref('CLT');
const activeBankOrderScope = ref('default');
const isSavingProductConfig = ref(false);

const emptyProductSettings = () =>
  CORBAN_PRODUCT_OPTIONS.reduce((result, product) => {
    result[product.value] = {
      enabled: false,
      method: 'GET',
      url: '',
      body: '{}',
      mapping: {
        name: '',
        birth_date: '',
        mother_name: '',
        available_margin: '',
      },
    };
    return result;
  }, {});

const normalizeProducts = products => {
  const normalized = emptyProductSettings();
  Object.entries(products || {}).forEach(([key, value]) => {
    if (!normalized[key]) return;

    normalized[key] = {
      enabled: Boolean(value?.enabled),
      method: value?.method || 'GET',
      url: value?.url || '',
      body: value?.body || '{}',
      mapping: {
        ...normalized[key].mapping,
        ...(value?.mapping || {}),
      },
    };
  });
  return normalized;
};

const normalizeBankProducts = hookSettings => {
  let fromList = [];
  if (Array.isArray(hookSettings?.produtos)) {
    fromList = hookSettings.produtos;
  } else if (Array.isArray(hookSettings?.products)) {
    fromList = hookSettings.products;
  }
  const fromSingle = hookSettings?.produto || hookSettings?.product;
  let candidateValues = fromList;
  if (!candidateValues.length && fromSingle) {
    candidateValues = [fromSingle];
  }
  const allowedValues = new Set(
    BANK_PRODUCT_OPTIONS.filter(
      option =>
        option.value === 'CLT' ||
        ['handplus', 'facta', 'v8', 'novo_saque'].includes(
          hookSettings?.provider
        )
    ).map(option => option.value)
  );

  return Array.from(
    new Set(
      (Array.isArray(candidateValues) ? candidateValues : [candidateValues])
        .flatMap(value =>
          String(value || '')
            .split(/[;,|]/)
            .map(item => item.trim().toUpperCase())
            .filter(Boolean)
        )
        .filter(value => allowedValues.has(value))
    )
  );
};

Object.assign(settings.products, emptyProductSettings());
settings.headers = [emptyRequestHeader('')];

const syncProviderWithOptions = (integrationId, options) => {
  if (props.integrationId !== integrationId) return;
  if (
    !settings.provider ||
    options.some(option => providerMatches(option.value, settings.provider))
  )
    return;

  settings.provider = options[0]?.value || '';
};

watch(erpProviderOptions, options => syncProviderWithOptions('erp', options), {
  immediate: true,
});

watch(
  corbanProviderOptions,
  options => syncProviderWithOptions('corban', options),
  { immediate: true }
);

const availableBankOptions = computed(() =>
  BANK_OPTIONS.filter(
    option =>
      option.value !== 'anchor' ||
      integration.value?.available_providers?.includes('anchor')
  )
);

const integrationConfig = computed(() => {
  if (props.integrationId === 'banking') {
    return {
      title: 'Integrações bancárias',
      description:
        'Conecte seus bancos com segurança e acompanhe o status de cada integração.',
      icon: 'i-lucide-landmark',
      providerLabel: t('INTEGRATION_APPS.LOCAL.BANKING.PROVIDER_LABEL'),
      providerPlaceholder: t(
        'INTEGRATION_APPS.LOCAL.BANKING.PROVIDER_PLACEHOLDER'
      ),
      providerOptions: availableBankOptions.value,
    };
  }

  if (props.integrationId === 'corban') {
    return {
      title: t('INTEGRATION_APPS.LOCAL.CORBAN.NAME'),
      description: t('INTEGRATION_APPS.LOCAL.CORBAN.CONFIG_DESCRIPTION'),
      icon: 'i-lucide-handshake',
      providerLabel: t('INTEGRATION_APPS.LOCAL.CORBAN.PROVIDER_LABEL'),
      providerPlaceholder: t(
        'INTEGRATION_APPS.LOCAL.CORBAN.PROVIDER_PLACEHOLDER'
      ),
      providerOptions: corbanProviderOptions.value,
    };
  }

  return {
    title: t('INTEGRATION_APPS.LOCAL.ERP.NAME'),
    description: t('INTEGRATION_APPS.LOCAL.ERP.CONFIG_DESCRIPTION'),
    icon: 'i-lucide-database',
    providerLabel: t('INTEGRATION_APPS.LOCAL.ERP.PROVIDER_LABEL'),
    providerPlaceholder: t('INTEGRATION_APPS.LOCAL.ERP.PROVIDER_PLACEHOLDER'),
    providerOptions: erpProviderOptions.value,
  };
});

const currentHook = computed(() => integration.value?.hooks?.[0]);
const isLoaded = computed(() => Boolean(integration.value?.id));
const usesProviderDialog = computed(() =>
  ['banking', 'corban', 'erp'].includes(props.integrationId)
);
const integrationHooks = computed(() => integration.value?.hooks || []);
const hasExistingMkauthHook = computed(() =>
  integrationHooks.value.some(hook => hook.settings?.provider === 'mkauth')
);

const allInboxes = useMapGetter('inboxes/getAllInboxes');
const inboxOptions = computed(() =>
  (allInboxes.value || [])
    .filter(inbox => inbox?.id)
    .map(inbox => ({
      value: Number(inbox.id),
      label: getInboxLabel(inbox) || `Caixa ${inbox.id}`,
    }))
);

const bankOrderScopeOptions = computed(() => [
  {
    value: 'default',
    label: 'Conta inteira',
    description: 'Ordem usada como padrão',
    icon: 'i-lucide-building-2',
  },
  ...inboxOptions.value.map(inbox => ({
    value: `inbox:${inbox.value}`,
    label: inbox.label,
    description: 'Caixa de entrada',
    icon: 'i-lucide-inbox',
  })),
]);

const bankOrderInboxScopeOptions = computed(() =>
  bankOrderScopeOptions.value.filter(option => option.value !== 'default')
);

const bankOrderInboxScopeValue = computed({
  get: () =>
    activeBankOrderScope.value.startsWith('inbox:')
      ? activeBankOrderScope.value
      : '',
  set: value => {
    if (value) activeBankOrderScope.value = String(value);
  },
});

const activeBankOrderScopeOption = computed(
  () =>
    bankOrderScopeOptions.value.find(
      option => option.value === activeBankOrderScope.value
    ) || bankOrderScopeOptions.value[0]
);

const priorityFlagInboxOptions = computed(() => [
  { value: '', label: 'Todas as caixas' },
  ...inboxOptions.value,
]);

const templateOptionsForInbox = inboxId => {
  if (!inboxId) return [];

  const filteredTemplates =
    store.getters['inboxes/getFilteredWhatsAppTemplates']?.(Number(inboxId)) ||
    [];
  const rawTemplates =
    store.getters['inboxes/getWhatsAppTemplates']?.(Number(inboxId)) || [];
  let templates = [];
  if (filteredTemplates.length) {
    templates = filteredTemplates;
  } else if (Array.isArray(rawTemplates)) {
    templates = rawTemplates;
  }

  return templates
    .filter(template => template?.name)
    .map(template => ({
      value: template.name,
      label: template.language
        ? `${template.name} (${template.language})`
        : template.name,
      language: template.language || '',
    }));
};

const selectedProviderOption = computed(() =>
  integrationConfig.value.providerOptions.find(option =>
    providerMatches(option.value, settings.provider)
  )
);

const providerDialogTitle = computed(() => {
  if (props.integrationId === 'banking' && selectedProviderOption.value) {
    const option = selectedProviderOption.value;
    const bankName = BANK_CARD_META[option.value]?.title || option.label;
    return `Configurar ${bankName}`;
  }

  return selectedProviderOption.value
    ? `${selectedProviderOption.value.label} - ${integrationConfig.value.title}`
    : integrationConfig.value.title;
});

const providerDialogWidth = computed(() => {
  if (props.integrationId === 'banking') return '3xl';
  return ['corban', 'erp'].includes(props.integrationId) ? '5xl' : '3xl';
});

const requiresCredentials = computed(
  () =>
    props.integrationId === 'banking' &&
    BANKS_WITH_CREDENTIALS.includes(settings.provider)
);

const usesClientCredentials = computed(
  () =>
    (props.integrationId === 'banking' &&
      CLIENT_CREDENTIAL_BANKS.includes(settings.provider)) ||
    (props.integrationId === 'erp' && settings.provider === 'mkauth')
);

const bullEnvironment = computed({
  get: () => settings.bullEnvironment,
  set: value => {
    const environment = value === 'staging' ? 'staging' : 'production';
    if (settings.bullEnvironment !== environment) {
      bankValidationState.valid = false;
    }
    settings.bullEnvironment = environment;
  },
});

const isProductionEnvironment = computed({
  get: () => bullEnvironment.value === 'production',
  set: enabled => {
    bullEnvironment.value = enabled ? 'production' : 'staging';
  },
});

const supportsAdditionalCredentialFields = computed(
  () =>
    requiresCredentials.value &&
    ![
      'v8',
      'presenca_bank',
      'bmg',
      'neon',
      'fintech_corban',
      ...CLIENT_CREDENTIAL_BANKS,
    ].includes(settings.provider)
);

const additionalCredentialLabel = computed(() => {
  if (settings.provider === 'bank321') return 'CPF certificado do digitador';
  if (settings.provider === 'c6') return 'Indicador Master';
  return `${t('INTEGRATION_APPS.LOCAL.TYPIST_CPF')} (opcional)`;
});

const additionalCredentialPlaceholder = computed(() =>
  settings.provider === 'c6' ? 'Código do indicador master' : 'CPF do vendedor'
);

const showLegacyMasterIndicatorField = computed(
  () =>
    supportsAdditionalCredentialFields.value &&
    !['c6', 'bank321'].includes(settings.provider)
);

const requiresApiUrl = computed(() => props.integrationId === 'erp');
const requiresApiKey = computed(() => {
  if (props.integrationId === 'erp') {
    return settings.provider === 'ixcsoft';
  }

  return (
    requiresCredentials.value === false ||
    ['fintech_corban', 'bank321'].includes(settings.provider)
  );
});

const shouldShowApiKeyField = computed(() => requiresApiKey.value);
const apiKeyLabel = computed(() => {
  if (['bank321', 'novo_saque'].includes(settings.provider)) return 'API Key';
  if (settings.provider === 'fintech_corban') {
    return 'Chave de assinatura (Subscription)';
  }
  if (settings.provider === 'ixcsoft') return 'API Key (Base64)';

  return t('INTEGRATION_APPS.LOCAL.API_KEY');
});

const isMkauthApiUrlValid = computed(() => {
  if (settings.provider !== 'mkauth') return true;

  try {
    return new URL(String(settings.apiUrl || '').trim()).protocol === 'https:';
  } catch {
    return false;
  }
});

const isErpApiUrlValid = computed(() => {
  if (settings.provider === 'ixcsoft') {
    return Boolean(normalizeIxcApiUrl(settings.apiUrl));
  }

  return isMkauthApiUrlValid.value;
});

const erpApiUrlError = computed(() => {
  if (
    !['ixcsoft', 'mkauth'].includes(settings.provider) ||
    !String(settings.apiUrl || '').trim() ||
    isErpApiUrlValid.value
  ) {
    return '';
  }

  return settings.provider === 'mkauth'
    ? localCopy.mkauthApiUrlInvalid
    : localCopy.ixcApiUrlInvalid;
});

const normalizeErpApiUrlField = () => {
  if (settings.provider === 'mkauth') {
    settings.apiUrl = String(settings.apiUrl || '').trim();
    return;
  }

  if (settings.provider !== 'ixcsoft') return;

  const normalizedUrl = normalizeIxcApiUrl(settings.apiUrl);
  if (normalizedUrl) settings.apiUrl = normalizedUrl;
};

const normalizeIxcApiKeyField = () => {
  if (settings.provider !== 'ixcsoft') return;

  settings.apiKey = normalizeIxcApiKey(settings.apiKey);
};

const normalizeIxcFields = () => {
  normalizeErpApiUrlField();
  normalizeIxcApiKeyField();
};

const showCorbanAdvancedFields = computed(() => false);

const factaProductsRequiringTable = computed(() => {
  if (props.integrationId !== 'banking' || settings.provider !== 'facta')
    return [];

  return settings.productsSelected.filter(product =>
    FACTA_PRODUCTS_WITH_TABLE.includes(product)
  );
});

const requiresFactaTable = computed(
  () =>
    props.integrationId === 'banking' &&
    settings.provider === 'facta' &&
    factaProductsRequiringTable.value.length > 0
);

const hasValidatedBankConnection = computed(() => {
  if (
    bankValidationState.provider === settings.provider &&
    bankValidationState.valid
  ) {
    return true;
  }

  /* eslint-disable no-use-before-define */
  return (
    currentProviderHook.value?.settings?.connection_validated === true ||
    currentProviderHook.value?.settings?.connection_validated === 'true'
  );
  /* eslint-enable no-use-before-define */
});

const activeFactaTableProduct = computed(() => {
  if (!requiresFactaTable.value) return '';

  if (
    factaProductsRequiringTable.value.includes(settings.factaActiveProductTab)
  ) {
    return settings.factaActiveProductTab;
  }

  return factaProductsRequiringTable.value[0] || '';
});

const activeFactaTables = computed(() => {
  const product = activeFactaTableProduct.value;
  if (!product) return [];
  return Array.isArray(settings.factaTablesByProduct[product])
    ? settings.factaTablesByProduct[product]
    : [];
});

const factaTableOptions = computed(() => {
  const mapByCode = new Map();

  activeFactaTables.value.forEach(table => {
    const code = String(table?.codigo || table?.codigo_tabela || '').trim();
    if (!code || mapByCode.has(code)) return;

    const description = String(
      table?.nome || table?.descricao || table?.descricao_tabela || ''
    ).trim();

    mapByCode.set(code, {
      value: code,
      label: description ? `${code} - ${description}` : code,
    });
  });

  // eslint-disable-next-line no-use-before-define
  const savedCode = String(factaTableInputValue.value || '').trim();
  if (savedCode && savedCode !== '__ALL__' && !mapByCode.has(savedCode)) {
    mapByCode.set(savedCode, {
      value: savedCode,
      label: `${savedCode} - tabela salva`,
    });
  }

  const options = Array.from(mapByCode.values());
  options.unshift({ value: '__ALL__', label: localCopy.factaTableAllTables });
  return options;
});

const factaTableInputValue = computed({
  get() {
    const product = activeFactaTableProduct.value;
    if (!product) return '';
    return settings.factaTableCodesByProduct[product] || '';
  },
  set(value) {
    const product = activeFactaTableProduct.value;
    if (!product) return;
    settings.factaTableCodesByProduct[product] = String(value || '').trim();
  },
});

const isFactaTableValid = computed(() => {
  if (!requiresFactaTable.value) return true;

  return factaProductsRequiringTable.value.every(
    product =>
      String(settings.factaTableCodesByProduct[product] || '').trim().length
  );
});

const canEnableBankingProvider = computed(() => {
  if (props.integrationId !== 'banking') return true;
  if (settings.provider === 'novo_saque')
    return Boolean(String(settings.apiKey || '').trim());
  if (!hasValidatedBankConnection.value) return false;
  return true;
});

const requiredFields = computed(() => {
  const fields = ['provider'];

  if (props.integrationId === 'erp') {
    if (settings.provider === 'mkauth') {
      const mkauthFields = ['provider', 'apiUrl', 'clientId'];
      if (!hasExistingMkauthHook.value) mkauthFields.push('clientSecret');
      return mkauthFields;
    }

    return ['provider', 'apiUrl', 'apiKey'];
  }

  if (props.integrationId === 'banking') {
    if (settings.provider === 'novo_saque')
      return settings.bullEnvironment === 'production'
        ? ['provider', 'apiKey', 'apiUrl']
        : ['provider', 'apiKey'];
    if (!requiresCredentials.value) return ['provider'];

    if (settings.provider === 'bank321')
      return ['provider', 'username', 'password', 'typistCpf', 'apiKey'];

    if (settings.provider === 'neon') {
      return ['provider', 'neonOrigin', 'neonTokenId'];
    }

    if (usesClientCredentials.value) {
      return ['provider', 'clientId', 'clientSecret'];
    }

    const credentialFields = ['provider', 'username', 'password'];
    if (settings.provider === 'v8') credentialFields.push('clientId');
    if (settings.provider === 'fintech_corban') {
      credentialFields.push('apiKey');
    }
    return credentialFields;
  }

  if (props.integrationId === 'corban') {
    return ['provider', 'apiKey', 'corbanHeaderName'];
  }

  if (requiresCredentials.value) {
    fields.push('username', 'password');
  }

  if (requiresApiKey.value) fields.push('apiKey');
  if (requiresApiUrl.value) fields.push('apiUrl');

  return fields;
});

const areCorbanProductsValid = computed(() => {
  if (props.integrationId !== 'corban') return true;
  return true;
});

const areCorbanHeadersValid = computed(() => {
  if (props.integrationId !== 'corban') return true;
  return true;
});

const areBankProductsValid = computed(() => {
  if (props.integrationId !== 'banking') return true;
  return settings.productsSelected.length > 0;
});

const isFormValid = computed(() => {
  const baseRequiredValid = requiredFields.value.every(field =>
    String(settings[field]).trim()
  );

  const invoiceNotificationsValid =
    props.integrationId !== 'erp' ||
    settings.provider !== 'ixcsoft' ||
    !settings.invoiceNotifications.enabled ||
    settings.invoiceNotifications.due_days.length > 0;

  if (props.integrationId === 'banking') {
    return (
      baseRequiredValid &&
      areBankProductsValid.value &&
      invoiceNotificationsValid &&
      isErpApiUrlValid.value
    );
  }

  return (
    baseRequiredValid &&
    areCorbanProductsValid.value &&
    areCorbanHeadersValid.value &&
    invoiceNotificationsValid &&
    isErpApiUrlValid.value
  );
});

const isValidationFormValid = computed(
  () =>
    requiredFields.value.every(field => String(settings[field]).trim()) &&
    (settings.provider !== 'neon' ||
      /^\d{11}$/.test(
        String(settings.neonValidationCpf || '').replace(/\D/g, '')
      )) &&
    areBankProductsValid.value &&
    areCorbanProductsValid.value &&
    areCorbanHeadersValid.value &&
    isErpApiUrlValid.value
);

function normalizeInvoiceRule(rule) {
  return {
    ...emptyInvoiceRule(),
    ...(rule || {}),
    enabled: Boolean(rule?.enabled),
    days: Number(rule?.days) || 0,
    inbox_id: rule?.inbox_id ? Number(rule.inbox_id) : null,
    template_name: rule?.template_name || '',
    template_language: rule?.template_language || '',
  };
}

function normalizeInvoiceDueDays(days) {
  return Array.from(
    new Set(
      (Array.isArray(days) ? days : String(days || '').split(/[;,|\s]+/))
        .map(day => Number(day))
        .filter(day => Number.isInteger(day) && day >= 1 && day <= 31)
    )
  ).sort((a, b) => a - b);
}

function normalizeInvoiceNotifications(value) {
  return {
    ...emptyInvoiceNotifications(),
    ...(value || {}),
    enabled: Boolean(value?.enabled),
    start_time: value?.start_time || '08:00',
    due_days: normalizeInvoiceDueDays(value?.due_days),
    message_interval_seconds: Number(value?.message_interval_seconds) || 30,
    before_due: normalizeInvoiceRule(value?.before_due),
    after_due: normalizeInvoiceRule(value?.after_due),
  };
}

const visibleSettings = computed(() => {
  if (props.integrationId === 'erp') {
    const isIxcSoft = settings.provider === 'ixcsoft';
    const payload = {
      provider: settings.provider,
      api_url: isIxcSoft
        ? normalizeIxcApiUrl(settings.apiUrl)
        : String(settings.apiUrl || '').trim(),
    };

    if (isIxcSoft) {
      payload.api_key = normalizeIxcApiKey(settings.apiKey);
    } else {
      payload.client_id = String(settings.clientId || '').trim();
      const clientSecret = String(settings.clientSecret || '').trim();
      if (clientSecret) payload.client_secret = clientSecret;
    }

    payload.customer_service_order = {
      enabled: Boolean(settings.customerServiceOrder.enabled),
    };

    if (isIxcSoft) {
      payload.customer_service_opening = {
        enabled: Boolean(settings.customerServiceOpening.enabled),
      };
      Object.assign(
        payload,
        buildIxcFinancialTrustUnlockSettings(settings.financialTrustUnlock)
      );
      payload.invoice_notifications = normalizeInvoiceNotifications(
        settings.invoiceNotifications
      );
    }

    return payload;
  }

  if (props.integrationId === 'corban') {
    const headerName = String(settings.corbanHeaderName || '').trim();
    const apiKey = String(settings.apiKey || '').trim();

    return {
      provider: settings.provider,
      api_key: apiKey,
      authorization_header_name: headerName,
      headers: headerName ? [{ key: headerName, value: apiKey }] : [],
      // The hook is declared below because it depends on the normalized
      // provider helpers, while this computed only runs after setup.
      // eslint-disable-next-line no-use-before-define
      products: currentProviderHook.value?.settings?.products || {},
    };
  }

  if (requiresCredentials.value || settings.provider === 'novo_saque') {
    const requiredFactaTableProducts =
      settings.provider === 'facta'
        ? settings.productsSelected.filter(product =>
            FACTA_PRODUCTS_WITH_TABLE.includes(product)
          )
        : [];

    const codigoTabelaPorProduto = requiredFactaTableProducts.reduce(
      (result, product) => {
        const code = String(
          settings.factaTableCodesByProduct[product] || ''
        ).trim();
        if (code) result[product] = code;
        return result;
      },
      {}
    );

    const factaTabelasPorProduto = requiredFactaTableProducts.reduce(
      (result, product) => {
        const tables = Array.isArray(settings.factaTablesByProduct[product])
          ? settings.factaTablesByProduct[product]
          : [];
        result[product] = tables;
        return result;
      },
      {}
    );

    const flattenedFactaTables = Object.values(factaTabelasPorProduto)
      .flat()
      .filter(Boolean);

    const legacyFactaTableCode =
      settings.provider === 'facta'
        ? requiredFactaTableProducts
            .map(product => codigoTabelaPorProduto[product])
            .find(Boolean) || ''
        : '';

    const payload = {
      provider: settings.provider,
      connection_validated: hasValidatedBankConnection.value,
    };

    if (settings.provider === 'novo_saque') {
      payload.api_key = String(settings.apiKey || '').trim();
      payload.environment =
        settings.bullEnvironment === 'production' ? 'production' : 'sandbox';
      payload.api_url =
        payload.environment === 'sandbox'
          ? 'https://api.novosaque.dev.br'
          : String(settings.apiUrl || '').trim();
      payload.connection_validated = false;
    } else if (settings.provider === 'neon') {
      payload.x_origin = String(settings.neonOrigin || '').trim();
      payload.token_id = String(settings.neonTokenId || '').trim();
    } else if (usesClientCredentials.value) {
      payload.client_id = settings.clientId;
      payload.client_secret = settings.clientSecret;
      if (settings.provider === 'soma') {
        payload.consulta_unificada = settings.somaConsultaUnificada === true;
        if (settings.somaSemSeguro === true) payload.com_seguro = false;
      }
    } else {
      payload.username = settings.username;
      payload.password = settings.password;
    }

    if (settings.provider === 'v8') {
      payload.client_id = settings.clientId;
    }

    if (
      ['bull', 'neon', 'fintech_corban', 'bank321', 'anchor'].includes(
        settings.provider
      )
    ) {
      payload.environment = settings.bullEnvironment;
    }

    if (settings.provider === 'neon') {
      payload.api_url =
        NEON_BASE_URLS[settings.bullEnvironment] || NEON_BASE_URLS.production;
    }

    if (settings.provider === 'bank321') {
      payload.api_key = String(settings.apiKey || '').trim();
    }

    if (settings.provider === 'fintech_corban') {
      payload.api_key = settings.apiKey;
      payload.api_url =
        FINTECH_CORBAN_BASE_URLS[settings.bullEnvironment] ||
        FINTECH_CORBAN_BASE_URLS.production;
    }

    if (settings.productsSelected.length) {
      payload.produto = settings.productsSelected[0];
      payload.produtos = settings.productsSelected;
    }

    if (settings.provider === 'facta') {
      payload.codigo_tabela = legacyFactaTableCode;
      payload.codigo_tabela_por_produto = codigoTabelaPorProduto;
      payload.facta_tabelas = flattenedFactaTables;
      payload.facta_tabelas_por_produto = factaTabelasPorProduto;
    }

    if (
      supportsAdditionalCredentialFields.value &&
      String(settings.typistCpf || '').trim()
    ) {
      payload.typist_cpf = settings.typistCpf;
    }

    if (
      supportsAdditionalCredentialFields.value &&
      settings.provider !== 'bank321' &&
      String(settings.masterIndicator || '').trim()
    ) {
      payload.master_indicator = settings.masterIndicator;
    }

    return payload;
  }

  const payload = {
    provider: settings.provider,
    connection_validated: hasValidatedBankConnection.value,
  };

  if (settings.productsSelected.length) {
    payload.produto = settings.productsSelected[0];
    payload.produtos = settings.productsSelected;
  }

  if (String(settings.apiKey || '').trim()) {
    payload.api_key = settings.apiKey;
  }

  return payload;
});

const hookPayload = computed(() => ({
  hook: {
    app_id: props.integrationId,
    status: settings.enabled ? 'enabled' : 'disabled',
    settings: visibleSettings.value,
  },
}));

const getProviderHook = provider =>
  integrationHooks.value.find(hook =>
    providerMatches(provider, hook.settings?.provider)
  );

const isHookEnabled = hook =>
  hook?.status === true || hook?.status === 'enabled';

const crossSellAvailable = computed(() =>
  integrationHooks.value.some(
    hook =>
      isHookEnabled(hook) &&
      ['handplus', 'facta', 'v8'].includes(hook.settings?.provider) &&
      [true, 'true'].includes(hook.settings?.connection_validated) &&
      ['CLT', 'FGTS'].every(product =>
        normalizeBankProducts(hook.settings || {}).includes(product)
      )
  )
);

const currentProviderHook = computed(() => getProviderHook(settings.provider));

const somaWebhookRegisteredAt = computed(() => {
  if (settings.provider !== 'soma') return '';

  const hook = currentProviderHook.value;
  const hookSettings = hook?.settings || {};
  const credentialsMatch =
    String(hookSettings.client_id || '') === String(settings.clientId || '') &&
    String(hookSettings.client_secret || '') ===
      String(settings.clientSecret || '');

  return credentialsMatch ? hookSettings.soma_webhook_registered_at || '' : '';
});

const hasRegisteredSomaWebhook = computed(() =>
  Boolean(somaWebhookRegisteredAt.value)
);

const formattedSomaWebhookRegisteredAt = computed(() => {
  if (!somaWebhookRegisteredAt.value) return '';

  const date = new Date(somaWebhookRegisteredAt.value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
});

const productConfigHook = computed(() =>
  integrationHooks.value.find(h => h.settings?.provider === '_product_config')
);

const ixcProviderHook = computed(() => getProviderHook('ixcsoft'));
const showIxcInvoiceReport = computed(
  () => props.integrationId === 'erp' && isHookEnabled(ixcProviderHook.value)
);

const activeBankOrderInboxId = computed(() => {
  if (!activeBankOrderScope.value.startsWith('inbox:')) return null;

  const inboxId = Number(activeBankOrderScope.value.split(':')[1]);
  return Number.isInteger(inboxId) && inboxId > 0 ? inboxId : null;
});

const configuredBankOrder = productValue => {
  const inboxId = activeBankOrderInboxId.value;
  if (!inboxId) return bankConfig.productOrder[productValue] || [];

  const inboxOrder = bankConfig.inboxProductOrder[String(inboxId)];
  return Array.isArray(inboxOrder?.[productValue])
    ? inboxOrder[productValue]
    : bankConfig.productOrder[productValue] || [];
};

const editableBankOrder = productValue => {
  const inboxId = activeBankOrderInboxId.value;
  if (!inboxId) return bankConfig.productOrder;

  const key = String(inboxId);
  if (!bankConfig.inboxProductOrder[key]) {
    bankConfig.inboxProductOrder[key] = {};
  }
  if (!Array.isArray(bankConfig.inboxProductOrder[key][productValue])) {
    bankConfig.inboxProductOrder[key][productValue] = [
      ...(bankConfig.productOrder[productValue] || []),
    ];
  }

  return bankConfig.inboxProductOrder[key];
};

const setConfiguredBankOrder = (productValue, order) => {
  editableBankOrder(productValue)[productValue] = order;
};

const productBanksView = computed(() => {
  if (props.integrationId !== 'banking') return [];

  return BANK_PRODUCT_OPTIONS.map(product => {
    const order = configuredBankOrder(product.value).map(String);

    const configuredHooks = integrationHooks.value.filter(hook => {
      if (hook.settings?.provider === '_product_config') return false;
      return normalizeBankProducts(hook.settings || {}).includes(product.value);
    });

    const sorted = [...configuredHooks].sort((a, b) => {
      const ia = order.indexOf(String(a.id));
      const ib = order.indexOf(String(b.id));
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });

    return { ...product, hooks: sorted };
  }).filter(p => p.hooks.length > 0);
});

const activeProductConfig = computed(
  () =>
    productBanksView.value.find(
      product => product.value === activeBankProductTab.value
    ) ||
    productBanksView.value[0] ||
    null
);

const isProviderEnabled = provider => isHookEnabled(getProviderHook(provider));

const isProviderConfigured = provider => Boolean(getProviderHook(provider));

const displayedProviderOptions = computed(
  () => integrationConfig.value.providerOptions
);

const bankingStats = computed(() =>
  integrationConfig.value.providerOptions.reduce(
    (stats, option) => {
      if (isProviderConfigured(option.value)) stats.configured += 1;
      if (isProviderEnabled(option.value)) stats.active += 1;
      return stats;
    },
    {
      total: integrationConfig.value.providerOptions.length,
      configured: 0,
      active: 0,
    }
  )
);

const connectedProviderClasses = {
  card: 'border-n-teal-7 bg-n-solid-1 shadow-sm ring-1 ring-n-teal-7/30 hover:border-n-teal-8 hover:shadow-md',
  badge: 'border border-n-teal-7/70 bg-n-teal-3/30 text-n-teal-11',
  status: 'border border-n-teal-7/60 bg-n-teal-3/40 text-n-teal-11',
  product: 'border-n-weak bg-n-alpha-2 text-n-slate-11',
};

const inactiveProviderClasses = {
  card: 'border-2 border-n-amber-7 bg-n-solid-1 shadow-sm hover:bg-n-amber-2/10 hover:shadow-md',
  badge: 'border border-n-amber-7/70 bg-n-amber-2/20 text-n-slate-12',
  status: 'border border-n-amber-7/70 bg-n-amber-2/20 text-n-amber-11',
  product: 'border-n-weak bg-n-alpha-2 text-n-slate-11',
};

const unconfiguredProviderClasses = {
  card: 'border-dashed border-n-strong bg-n-solid-2 hover:border-n-slate-8 hover:bg-n-alpha-1 hover:shadow-md',
  badge: 'border border-n-weak bg-n-alpha-2 text-n-slate-11',
  status: 'border border-dashed border-n-strong bg-transparent text-n-slate-9',
  product: 'border-n-weak bg-transparent text-n-slate-10',
};

const providerVisualClasses = optionValue => {
  if (isProviderEnabled(optionValue)) return connectedProviderClasses;
  if (isProviderConfigured(optionValue)) return inactiveProviderClasses;
  return unconfiguredProviderClasses;
};

const providerCardClass = optionValue =>
  providerVisualClasses(optionValue).card;

const providerBadgeClass = optionValue =>
  providerVisualClasses(optionValue).badge;

const providerStatusPillClass = optionValue =>
  providerVisualClasses(optionValue).status;

const providerProductPillClass = optionValue =>
  providerVisualClasses(optionValue).product;

const bankCardTitle = option =>
  BANK_CARD_META[option?.value]?.title || option?.label || '';

const providerStatusClass = computed(() =>
  settings.enabled
    ? 'border-n-teal-7 bg-n-teal-3/20'
    : 'border-n-weak bg-n-alpha-1'
);

const toggleProviderStatus = () => {
  if (!settings.enabled && !canEnableBankingProvider.value) {
    useAlert(localCopy.validationRequiredToEnable);
    return;
  }
  settings.enabled = !settings.enabled;
};

const getProviderProduct = provider => {
  const hook = getProviderHook(provider);
  if (!hook) return '';

  if (props.integrationId === 'banking') {
    const products = normalizeBankProducts(hook.settings || {});
    if (!products.length) return '';
    const labels = products.map(
      product =>
        BANK_PRODUCT_OPTIONS.find(option => option.value === product)?.label ||
        product
    );
    return labels.join(', ');
  }

  if (props.integrationId === 'corban') {
    return CORBAN_PRODUCT_OPTIONS.filter(
      product => hook?.settings?.products?.[product.value]?.enabled
    )
      .map(product => product.label)
      .join(', ');
  }

  return hook?.settings?.produto || hook?.settings?.product || '';
};

const bankCards = computed(() =>
  availableBankOptions.value.map(option => ({
    ...option,
    title: bankCardTitle(option),
    description: BANK_CARD_META[option.value]?.subtitle || '',
    beta: BANK_CARD_META[option.value]?.beta,
    isNew: BANK_CARD_META[option.value]?.isNew === true,
    configured: isProviderConfigured(option.value),
    enabled: isProviderEnabled(option.value),
    products: getProviderProduct(option.value),
  }))
);

const applyFactaTableSettings = hookSettings => {
  const selectedProducts = normalizeBankProducts(hookSettings || {});
  const requiredProducts = selectedProducts.filter(product =>
    FACTA_PRODUCTS_WITH_TABLE.includes(product)
  );

  const incomingCodes =
    hookSettings?.codigo_tabela_por_produto ||
    hookSettings?.codigo_tabela_map ||
    {};

  const tableCodesByProduct = {
    FGTS: String(incomingCodes?.FGTS || '').trim(),
    INSS: String(incomingCodes?.INSS || '').trim(),
  };

  const legacyCode = String(hookSettings?.codigo_tabela || '').trim();
  if (legacyCode) {
    const firstMissing = requiredProducts.find(
      product => !tableCodesByProduct[product]
    );
    if (firstMissing) {
      tableCodesByProduct[firstMissing] = legacyCode;
    }
  }

  const incomingTablesByProduct = hookSettings?.facta_tabelas_por_produto || {};
  const legacyTables = Array.isArray(hookSettings?.facta_tabelas)
    ? hookSettings.facta_tabelas
    : [];

  const tablesByProduct = {
    FGTS: Array.isArray(incomingTablesByProduct?.FGTS)
      ? incomingTablesByProduct.FGTS
      : [],
    INSS: Array.isArray(incomingTablesByProduct?.INSS)
      ? incomingTablesByProduct.INSS
      : [],
  };

  if (legacyTables.length) {
    requiredProducts.forEach(product => {
      if (!tablesByProduct[product].length) {
        tablesByProduct[product] = legacyTables;
      }
    });
  }

  settings.factaTableCodesByProduct = tableCodesByProduct;
  settings.factaTablesByProduct = tablesByProduct;
  settings.factaActiveProductTab =
    requiredProducts[0] || settings.factaActiveProductTab || 'FGTS';
  settings.factaTableCode =
    requiredProducts
      .map(product => tableCodesByProduct[product])
      .find(Boolean) || legacyCode;
  settings.factaTables = requiredProducts
    .flatMap(product => tablesByProduct[product] || [])
    .filter(Boolean);
};

const applyInvoiceNotificationSettings = hookSettings => {
  settings.invoiceNotifications = normalizeInvoiceNotifications(
    hookSettings?.invoice_notifications
  );
};

const applyCustomerServiceOrderSettings = hookSettings => {
  settings.customerServiceOrder = {
    ...emptyCustomerServiceOrder(),
    ...(hookSettings?.customer_service_order || {}),
    enabled: Boolean(hookSettings?.customer_service_order?.enabled),
  };
};

const applyCustomerServiceOpeningSettings = hookSettings => {
  settings.customerServiceOpening = {
    ...emptyCustomerServiceOpening(),
    ...(hookSettings?.customer_service_opening || {}),
    enabled: Boolean(hookSettings?.customer_service_opening?.enabled),
  };
};

const applyFinancialTrustUnlockSettings = hookSettings => {
  settings.financialTrustUnlock = buildIxcFinancialTrustUnlockSettings(
    hookSettings?.financial_trust_unlock
  ).financial_trust_unlock;
};

const syncInvoiceTemplateLanguage = ruleKey => {
  const rule = settings.invoiceNotifications[ruleKey];
  const option = templateOptionsForInbox(rule.inbox_id).find(
    item => item.value === rule.template_name
  );
  rule.template_language = option?.language || '';
};

const resetInvoiceTemplateForInbox = ruleKey => {
  const rule = settings.invoiceNotifications[ruleKey];
  rule.inbox_id = rule.inbox_id ? Number(rule.inbox_id) : null;
  rule.template_name = '';
  rule.template_language = '';
};

const toggleBankProduct = productValue => {
  settings.productsSelected = settings.productsSelected.includes(productValue)
    ? settings.productsSelected.filter(value => value !== productValue)
    : [...settings.productsSelected, productValue];
};

const invoiceInboxOptions = computed(() => [
  { value: '', label: localCopy.invoiceInboxPlaceholder },
  ...inboxOptions.value,
]);

const invoiceTemplateOptions = inboxId => [
  { value: '', label: localCopy.invoiceTemplatePlaceholder },
  ...templateOptionsForInbox(inboxId),
];

const toggleInvoiceDueDay = day => {
  const currentDays = normalizeInvoiceDueDays(
    settings.invoiceNotifications.due_days
  );
  settings.invoiceNotifications.due_days = currentDays.includes(day)
    ? currentDays.filter(item => item !== day)
    : normalizeInvoiceDueDays([...currentDays, day]);
};

const resetSettings = provider => {
  settings.enabled = false;
  settings.provider = provider || '';
  settings.productsSelected = [];
  settings.factaTableCode = '';
  settings.factaTables = [];
  settings.factaTableCodesByProduct = {
    FGTS: '',
    INSS: '',
  };
  settings.factaTablesByProduct = {
    FGTS: [],
    INSS: [],
  };
  settings.factaActiveProductTab = 'FGTS';
  bankValidationState.provider = provider || '';
  bankValidationState.valid = false;
  settings.username = '';
  settings.password = '';
  settings.clientId = '';
  settings.clientSecret = '';
  settings.somaConsultaUnificada = false;
  settings.somaSemSeguro = false;
  settings.bullEnvironment = ['novo_saque', 'anchor'].includes(provider)
    ? 'staging'
    : 'production';
  settings.neonOrigin = '';
  settings.neonTokenId = '';
  settings.neonValidationCpf = '';
  settings.typistCpf = '';
  settings.masterIndicator = '';
  settings.corbanHeaderName = DEFAULT_CORBAN_HEADER_BY_PROVIDER[provider] || '';
  settings.apiKey = '';
  settings.headers = [emptyRequestHeader(provider)];
  settings.apiUrl = '';
  settings.customerServiceOrder = emptyCustomerServiceOrder();
  settings.customerServiceOpening = emptyCustomerServiceOpening();
  settings.financialTrustUnlock = emptyFinancialTrustUnlock();
  settings.invoiceNotifications = emptyInvoiceNotifications();
  Object.assign(settings.products, emptyProductSettings());
};

const loadHook = () => {
  const hook = currentHook.value;
  if (!hook) return;

  const provider = hook.settings?.provider || '';

  settings.enabled = isHookEnabled(hook);
  settings.provider = provider;
  settings.productsSelected = normalizeBankProducts(hook.settings || {});
  applyFactaTableSettings(hook.settings || {});
  settings.username = hook.settings?.username || '';
  settings.password = hook.settings?.password || '';
  settings.clientId = hook.settings?.client_id || '';
  settings.clientSecret = hook.settings?.client_secret || '';
  settings.somaConsultaUnificada = hook.settings?.consulta_unificada === true;
  settings.somaSemSeguro = hook.settings?.com_seguro === false;
  settings.neonOrigin = hook.settings?.x_origin || '';
  settings.neonTokenId = hook.settings?.token_id || '';
  settings.neonValidationCpf = '';
  settings.bullEnvironment =
    hook.settings?.environment === 'sandbox' ||
    hook.settings?.environment === 'staging' ||
    hook.settings?.is_homol === true ||
    hook.settings?.is_homol === 'true'
      ? 'staging'
      : 'production';
  settings.typistCpf = hook.settings?.typist_cpf || '';
  settings.masterIndicator = hook.settings?.master_indicator || '';
  settings.apiKey =
    hook.settings?.api_key || hook.settings?.headers?.[0]?.value || '';
  settings.corbanHeaderName =
    hook.settings?.authorization_header_name ||
    hook.settings?.headers?.[0]?.key ||
    DEFAULT_CORBAN_HEADER_BY_PROVIDER[provider] ||
    '';
  settings.headers = resolveCorbanHeaders(hook.settings || {}, provider);
  settings.apiUrl = hook.settings?.api_url || '';
  applyCustomerServiceOrderSettings(hook.settings || {});
  applyCustomerServiceOpeningSettings(hook.settings || {});
  applyFinancialTrustUnlockSettings(hook.settings || {});
  applyInvoiceNotificationSettings(hook.settings || {});
  bankValidationState.provider = provider;
  bankValidationState.valid =
    hook.settings?.connection_validated === true ||
    hook.settings?.connection_validated === 'true';
  Object.assign(settings.products, normalizeProducts(hook.settings?.products));
};

const addCorbanHeader = () => {
  settings.headers.push(emptyRequestHeader(settings.provider));
};

const removeCorbanHeader = index => {
  settings.headers.splice(index, 1);
  if (!settings.headers.length) {
    settings.headers.push(emptyRequestHeader(settings.provider));
  }
};

function productLabel(productKey) {
  return (
    CORBAN_PRODUCT_OPTIONS.find(product => product.value === productKey)
      ?.label || productKey
  );
}

function corbanValidationError(data) {
  const productError = Object.entries(data?.products || {}).find(
    ([, result]) => !result?.ok
  );

  if (!productError) return localCopy.mappingNotDetected;

  const [productKey, result] = productError;
  const details = [
    productLabel(productKey),
    result?.http_status ? `${localCopy.httpStatus} ${result.http_status}` : '',
    result?.error || result?.message || '',
  ].filter(Boolean);

  return details.length
    ? `${localCopy.validationError}: ${details.join(' - ')}`
    : localCopy.mappingNotDetected;
}

function extractRequestErrorMessage(error) {
  const payload = error?.response?.data;

  if (payload?.products) {
    return corbanValidationError(payload);
  }

  const details = [
    payload?.error,
    payload?.message,
    payload?.detail,
    payload?.details,
    error?.message,
  ].filter(Boolean);

  if (!details.length) return localCopy.validationError;

  const [primary, ...rest] = details;
  return rest.length
    ? `${primary} | ${localCopy.connectionReason}: ${rest.join(' | ')}`
    : primary;
}

function applyReviewedMapping() {
  Object.entries(mappingReview.products || {}).forEach(
    ([productKey, productReview]) => {
      if (!settings.products[productKey]) return;

      settings.products[productKey].mapping = {
        ...settings.products[productKey].mapping,
        ...(productReview.mapping || {}),
      };
    }
  );

  mappingReviewDialogRef.value?.close();
}

function validationRequiredMessage() {
  if (!isErpApiUrlValid.value) {
    return settings.provider === 'mkauth'
      ? localCopy.mkauthApiUrlInvalid
      : localCopy.ixcApiUrlInvalid;
  }

  if (props.integrationId === 'banking' && !isFactaTableValid.value) {
    return localCopy.factaTableRequiredError;
  }

  return t('INTEGRATION_APPS.LOCAL.VALIDATION_REQUIRED');
}

const consultFactaTables = async () => {
  if (!isValidationFormValid.value) {
    useAlert(t('INTEGRATION_APPS.LOCAL.VALIDATION_REQUIRED'));
    return;
  }

  if (!requiresFactaTable.value) {
    useAlert(localCopy.factaTableSelectProductHint);
    return;
  }

  isConsultingFactaTables.value = true;
  const failures = [];

  try {
    // eslint-disable-next-line no-restricted-syntax
    for (const product of factaProductsRequiringTable.value) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await IntegrationsAPI.bankingTables({
          data: {
            ...visibleSettings.value,
            produto: product,
            produtos: [product],
          },
        });
        const tables = Array.isArray(data?.tables) ? data.tables : [];
        settings.factaTablesByProduct[product] = tables;

        if (
          !settings.factaTableCodesByProduct[product] &&
          tables.length === 1
        ) {
          settings.factaTableCodesByProduct[product] =
            tables[0]?.codigo || tables[0]?.codigo_tabela || '';
        }
      } catch (error) {
        failures.push(`${product}: ${extractRequestErrorMessage(error)}`);
      }
    }

    settings.factaTableCode =
      factaProductsRequiringTable.value
        .map(product => settings.factaTableCodesByProduct[product])
        .find(Boolean) || '';
    settings.factaTables = factaProductsRequiringTable.value
      .flatMap(product => settings.factaTablesByProduct[product] || [])
      .filter(Boolean);

    if (failures.length) {
      useAlert(`${localCopy.factaTableConsultPartial} ${failures.join(' | ')}`);
    } else {
      useAlert(localCopy.factaTableConsultSuccess, { variant: 'success' });
    }
  } finally {
    isConsultingFactaTables.value = false;
  }
};

const consultC6TypistCode = async () => {
  if (!String(settings.username || '').trim() || !settings.password) {
    useAlert(localCopy.c6TypistCodeCredentialsRequired, { variant: 'error' });
    return;
  }

  isConsultingC6TypistCode.value = true;
  try {
    const { data } = await IntegrationsAPI.c6TypistCode({
      data: {
        username: settings.username,
        password: settings.password,
      },
    });
    const typistCode = String(data?.typist_code || '').trim();

    if (!data?.ok || !typistCode) {
      const consultationError = new Error(
        data?.error || data?.message || 'O C6 não retornou um código válido.'
      );
      consultationError.response = { data };
      throw consultationError;
    }

    settings.typistCpf = typistCode;
    useAlert(`${localCopy.c6TypistCodeSuccess} Código: ${typistCode}`, {
      variant: 'success',
    });
  } catch (error) {
    const requestTimedOut =
      error?.code === 'ECONNABORTED' ||
      String(error?.message || '')
        .toLowerCase()
        .includes('timeout');
    useAlert(
      requestTimedOut
        ? localCopy.c6TypistCodeTimeout
        : extractRequestErrorMessage(error),
      { variant: 'error' }
    );
  } finally {
    isConsultingC6TypistCode.value = false;
  }
};

const validateConnection = async () => {
  if (!isValidationFormValid.value) {
    useAlert(t('INTEGRATION_APPS.LOCAL.VALIDATION_REQUIRED'));
    return;
  }

  if (props.integrationId === 'corban') {
    isValidatingConnection.value = true;
    try {
      useAlert(localCopy.validationSuccess, { variant: 'success' });
    } catch (error) {
      useAlert(extractRequestErrorMessage(error), { variant: 'error' });
    } finally {
      isValidatingConnection.value = false;
    }
    return;
  }

  if (props.integrationId !== 'banking') {
    useAlert(t('INTEGRATION_APPS.LOCAL.VALIDATION_SUCCESS'));
    return;
  }

  isValidatingConnection.value = true;
  try {
    const validationData =
      settings.provider === 'neon'
        ? {
            ...visibleSettings.value,
            validation_cpf: String(settings.neonValidationCpf || '').replace(
              /\D/g,
              ''
            ),
          }
        : visibleSettings.value;
    const { data } = await IntegrationsAPI.validateBanking({
      data: validationData,
    });
    const validated = Boolean(data?.ok);
    bankValidationState.provider = settings.provider;
    bankValidationState.valid = validated;

    if (!validated) {
      const validationError = new Error(
        data?.error || data?.message || localCopy.validationError
      );
      validationError.response = { data };
      throw validationError;
    }

    if (validated && settings.provider === 'facta') {
      const activeProduct = activeFactaTableProduct.value;
      const returnedTables = Array.isArray(data?.tables) ? data.tables : [];
      if (activeProduct) {
        settings.factaTablesByProduct[activeProduct] = returnedTables;
        if (
          !settings.factaTableCodesByProduct[activeProduct] &&
          returnedTables.length === 1
        ) {
          settings.factaTableCodesByProduct[activeProduct] =
            returnedTables[0]?.codigo || returnedTables[0]?.codigo_tabela || '';
        }
      }

      settings.factaTableCode =
        factaProductsRequiringTable.value
          .map(product => settings.factaTableCodesByProduct[product])
          .find(Boolean) || '';
      settings.factaTables = factaProductsRequiringTable.value
        .flatMap(product => settings.factaTablesByProduct[product] || [])
        .filter(Boolean);
    }

    const validatedSettings = {
      ...visibleSettings.value,
      connection_validated: validated,
    };

    if (settings.provider === 'facta') {
      validatedSettings.codigo_tabela_por_produto =
        visibleSettings.value.codigo_tabela_por_produto || {};
      validatedSettings.facta_tabelas_por_produto =
        visibleSettings.value.facta_tabelas_por_produto || {};
      validatedSettings.facta_tabelas = settings.factaTables;
    }

    const hook = currentProviderHook.value;
    if (hook?.id) {
      await store.dispatch('integrations/updateHook', {
        hookId: hook.id,
        hookData: {
          hook: {
            app_id: props.integrationId,
            status: settings.enabled ? 'enabled' : 'disabled',
            settings: validatedSettings,
          },
        },
      });
    }

    useAlert(localCopy.validationSuccess, { variant: 'success' });
  } catch (error) {
    useAlert(extractRequestErrorMessage(error), { variant: 'error' });
  } finally {
    isValidatingConnection.value = false;
  }
};

const registerSomaWebhook = async () => {
  const hook = currentProviderHook.value;
  if (!hook?.id) {
    useAlert(localCopy.somaWebhookSaveFirst, { variant: 'error' });
    return;
  }

  if (
    settings.provider !== 'soma' ||
    !String(settings.clientId).trim() ||
    !String(settings.clientSecret).trim()
  ) {
    useAlert('Informe o Client ID e o Client Secret da Soma.', {
      variant: 'error',
    });
    return;
  }

  isRegisteringSomaWebhook.value = true;
  try {
    const { data } = await IntegrationsAPI.registerSomaWebhook({
      data: visibleSettings.value,
    });
    if (!data?.ok) {
      const registrationError = new Error(
        data?.error || data?.message || localCopy.validationError
      );
      registrationError.response = { data };
      throw registrationError;
    }

    await store.dispatch('integrations/showHook', data.hook_id || hook.id);

    useAlert(data?.message || localCopy.somaWebhookSuccess, {
      variant: 'success',
    });
  } catch (error) {
    useAlert(extractRequestErrorMessage(error), { variant: 'error' });
  } finally {
    isRegisteringSomaWebhook.value = false;
  }
};

const saveSettings = async () => {
  normalizeIxcFields();

  if (!isFormValid.value) {
    useAlert(validationRequiredMessage());
    return;
  }

  try {
    const hook = usesProviderDialog.value
      ? getProviderHook(settings.provider)
      : currentHook.value;

    if (hook?.id) {
      await store.dispatch('integrations/updateHook', {
        hookId: hook.id,
        hookData: hookPayload.value,
      });
    } else {
      await store.dispatch('integrations/createHook', hookPayload.value);
    }
    useAlert(t('INTEGRATION_APPS.LOCAL.SAVE_SUCCESS'), { variant: 'success' });
    providerDialogRef.value?.close();
  } catch (error) {
    useAlert(extractRequestErrorMessage(error), { variant: 'error' });
  }
};

const deleteSettings = async () => {
  const hook = currentProviderHook.value;
  if (!hook?.id) return;

  try {
    await store.dispatch('integrations/deleteHook', {
      appId: props.integrationId,
      hookId: hook.id,
    });
    resetSettings(settings.provider);
    providerDialogRef.value?.close();
    useAlert(localCopy.deleteSuccess, { variant: 'success' });
  } catch (error) {
    useAlert(localCopy.deleteError, { variant: 'error' });
  }
};

const goBack = () => {
  router.push({ name: 'settings_applications' });
};

const loadProductConfig = () => {
  const hook = productConfigHook.value;
  if (!hook) return;

  bankConfig.crossSell = {
    enabled: hook.settings?.cross_sell?.enabled === true,
    primary: hook.settings?.cross_sell?.order?.[0] === 'FGTS' ? 'FGTS' : 'CLT',
  };
  const order = hook.settings?.product_order || {};
  const inboxOrder = hook.settings?.inbox_product_order || {};
  const priorityFlags = hook.settings?.priority_flags || [];
  const followup = hook.settings?.product_followup || {};
  const offerSending = hook.settings?.offer_sending || {};

  bankConfig.inboxProductOrder = Object.entries(inboxOrder).reduce(
    (result, [inboxId, products]) => {
      result[String(inboxId)] = BANK_PRODUCT_OPTIONS.reduce(
        (productResult, { value }) => {
          if (Array.isArray(products?.[value])) {
            productResult[value] = products[value].map(String);
          }
          return productResult;
        },
        {}
      );
      return result;
    },
    {}
  );
  bankConfig.priorityFlags = Array.isArray(priorityFlags)
    ? priorityFlags.map(rule => ({
        flag: String(rule?.flag || ''),
        hook_id: Number(rule?.hook_id) || '',
        product: ['FGTS', 'CLT'].includes(rule?.product) ? rule.product : 'CLT',
        inbox_id: Number(rule?.inbox_id) || '',
      }))
    : [];

  BANK_PRODUCT_OPTIONS.forEach(({ value: pv }) => {
    bankConfig.productOrder[pv] = Array.isArray(order[pv])
      ? order[pv].map(String)
      : [];
    const fu = followup[pv] || {};
    // Migrate from old single-object format to new array format
    let items;
    if (Array.isArray(fu.items)) {
      items = fu.items.map(i => ({
        delay_value: Number(i.delay_value) || 1440,
        delay_unit: i.delay_unit || 'minutes',
        message: i.message || '',
      }));
    } else if (fu.delay_minutes || fu.message) {
      items = [
        {
          delay_value: Number(fu.delay_minutes) || 1440,
          delay_unit: 'minutes',
          message: fu.message || '',
        },
      ];
    } else {
      items = [];
    }
    bankConfig.productFollowup[pv] = {
      enabled: Boolean(fu.enabled),
      items,
    };

    const offer = offerSending[pv] || {};
    bankConfig.offerSending[pv] = {
      ...emptyOfferSending(),
      ...offer,
      enabled: Boolean(offer.enabled),
      delay_value:
        Number(offer.delay_value) || Number(offer.delay_minutes) || 60,
      delay_unit: offer.delay_unit || 'minutes',
      mode: ['auto', 'manual'].includes(offer.mode) ? offer.mode : 'auto',
      table_code: offer.table_code || offer.codigo_tabela || '',
      message: offer.message || '',
    };
  });
};

const addFollowup = productValue => {
  bankConfig.productFollowup[productValue].items.push({
    delay_value: 1440,
    delay_unit: 'minutes',
    message: '',
  });
};

const removeFollowup = (productValue, index) => {
  bankConfig.productFollowup[productValue].items.splice(index, 1);
};

const delayMinutes = item => {
  const v = Number(item.delay_value) || 1;
  if (item.delay_unit === 'hours') return v * 60;
  if (item.delay_unit === 'days') return v * 1440;
  return v;
};

const followupDelayMinutes = item => delayMinutes(item);

const ensureProductOrder = productValue => {
  const existing = configuredBankOrder(productValue).map(String);
  const allIds = (
    productBanksView.value.find(p => p.value === productValue)?.hooks || []
  ).map(h => String(h.id));

  allIds.forEach(id => {
    if (!existing.includes(id)) existing.push(id);
  });

  setConfiguredBankOrder(productValue, existing);
  return existing;
};

const moveBankUp = (productValue, hookId) => {
  const order = ensureProductOrder(productValue);
  const idx = order.indexOf(String(hookId));
  if (idx > 0) {
    [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
  }
};

const moveBankDown = (productValue, hookId) => {
  const order = ensureProductOrder(productValue);
  const idx = order.indexOf(String(hookId));
  if (idx !== -1 && idx < order.length - 1) {
    [order[idx + 1], order[idx]] = [order[idx], order[idx + 1]];
  }
};

const startBankDrag = (productValue, hookId, event) => {
  draggedBank.value = {
    productValue,
    hookId: String(hookId),
    originalOrder: [...ensureProductOrder(productValue)],
  };
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(hookId));
  }
};

const previewBankPosition = (productValue, targetHookId) => {
  if (
    !draggedBank.value ||
    draggedBank.value.productValue !== productValue ||
    draggedBank.value.hookId === String(targetHookId)
  ) {
    return;
  }

  const order = ensureProductOrder(productValue);
  const fromIndex = order.indexOf(draggedBank.value.hookId);
  const targetIndex = order.indexOf(String(targetHookId));
  if (fromIndex === -1 || targetIndex === -1) return;

  const [moved] = order.splice(fromIndex, 1);
  order.splice(targetIndex, 0, moved);
  setConfiguredBankOrder(productValue, [...order]);
};

const dropBank = () => {
  draggedBank.value = null;
};

const endBankDrag = () => {
  if (draggedBank.value) {
    setConfiguredBankOrder(draggedBank.value.productValue, [
      ...draggedBank.value.originalOrder,
    ]);
  }
  draggedBank.value = null;
};

const priorityFlagBankOptions = productValue => [
  { value: '', label: 'Selecione o banco' },
  ...integrationHooks.value
    .filter(hook => {
      if (hook.settings?.provider === '_product_config') return false;
      return normalizeBankProducts(hook.settings || {}).includes(productValue);
    })
    .map(hook => ({
      value: Number(hook.id),
      label:
        BANK_OPTIONS.find(bank => bank.value === hook.settings?.provider)
          ?.label || hook.settings?.provider,
    })),
];

const addPriorityFlag = () => {
  bankConfig.priorityFlags.push({
    flag: '',
    hook_id: '',
    product: activeBankProductTab.value || 'CLT',
    inbox_id: activeBankOrderInboxId.value || '',
  });
};

const removePriorityFlag = index => {
  bankConfig.priorityFlags.splice(index, 1);
};

const serializedPriorityFlags = () =>
  bankConfig.priorityFlags.map(rule => ({
    flag: String(rule.flag || '').trim(),
    hook_id: Number(rule.hook_id),
    product: rule.product || 'CLT',
    ...(Number(rule.inbox_id) > 0 ? { inbox_id: Number(rule.inbox_id) } : {}),
  }));

const validPriorityFlags = rules => {
  const complete = rules.every(
    rule =>
      rule.flag &&
      Number.isInteger(rule.hook_id) &&
      rule.hook_id > 0 &&
      ['FGTS', 'CLT'].includes(rule.product)
  );
  if (!complete) {
    useAlert('Preencha a flag, o produto e o banco de todas as regras.');
    return false;
  }

  const keys = rules.map(
    rule =>
      `${rule.inbox_id || 'all'}:${rule.product}:${rule.flag.toLocaleLowerCase(
        'pt-BR'
      )}`
  );
  if (new Set(keys).size !== keys.length) {
    useAlert('Existem flags repetidas para a mesma caixa e produto.');
    return false;
  }

  return true;
};

const saveProductConfig = async () => {
  if (isSavingProductConfig.value) return;

  const priorityFlags = serializedPriorityFlags();
  if (!validPriorityFlags(priorityFlags)) return;

  isSavingProductConfig.value = true;
  const serializedFollowup = {};
  Object.entries(bankConfig.productFollowup).forEach(([pv, fu]) => {
    serializedFollowup[pv] = {
      enabled: fu.enabled,
      items: (fu.items || []).map(item => ({
        delay_value: Number(item.delay_value) || 1,
        delay_unit: item.delay_unit || 'minutes',
        delay_minutes: followupDelayMinutes(item),
        message: item.message || '',
      })),
    };
  });

  const serializedOfferSending = {};
  Object.entries(bankConfig.offerSending).forEach(([pv, offer]) => {
    serializedOfferSending[pv] = {
      enabled: Boolean(offer.enabled),
      delay_value: Number(offer.delay_value) || 1,
      delay_unit: offer.delay_unit || 'minutes',
      delay_minutes: delayMinutes(offer),
      mode: offer.mode === 'manual' ? 'manual' : 'auto',
      table_code: offer.mode === 'manual' ? offer.table_code || '' : '',
      message: offer.message || '',
    };
  });

  const payload = {
    hook: {
      app_id: 'banking',
      status: 'enabled',
      settings: {
        provider: '_product_config',
        cross_sell: {
          enabled: bankConfig.crossSell.enabled,
          order:
            bankConfig.crossSell.primary === 'FGTS'
              ? ['FGTS', 'CLT']
              : ['CLT', 'FGTS'],
        },
        product_order: bankConfig.productOrder,
        inbox_product_order: bankConfig.inboxProductOrder,
        priority_flags: priorityFlags,
        product_followup: serializedFollowup,
        offer_sending: serializedOfferSending,
      },
    },
  };

  try {
    if (productConfigHook.value?.id) {
      await store.dispatch('integrations/updateHook', {
        hookId: productConfigHook.value.id,
        hookData: payload,
      });
    } else {
      await store.dispatch('integrations/createHook', payload);
    }
    useAlert(localCopy.saveProductConfigSuccess, { variant: 'success' });
  } catch {
    useAlert(localCopy.saveProductConfigError);
  } finally {
    isSavingProductConfig.value = false;
  }
};

const selectProvider = provider => {
  settings.provider = provider;
};

const openProviderSettings = option => {
  if (!usesProviderDialog.value) {
    selectProvider(option.value);
    return;
  }

  const hook = getProviderHook(option.value);

  if (!hook) {
    resetSettings(option.value);
  } else {
    settings.enabled = isHookEnabled(hook);
    settings.provider = option.value;
    settings.productsSelected = normalizeBankProducts(hook.settings || {});
    applyFactaTableSettings(hook.settings || {});
    settings.username = hook.settings?.username || '';
    settings.password = hook.settings?.password || '';
    settings.clientId = hook.settings?.client_id || '';
    settings.clientSecret = hook.settings?.client_secret || '';
    settings.somaConsultaUnificada = hook.settings?.consulta_unificada === true;
    settings.somaSemSeguro = hook.settings?.com_seguro === false;
    settings.neonOrigin = hook.settings?.x_origin || '';
    settings.neonTokenId = hook.settings?.token_id || '';
    settings.neonValidationCpf = '';
    settings.bullEnvironment =
      hook.settings?.environment === 'sandbox' ||
      hook.settings?.environment === 'staging' ||
      hook.settings?.is_homol === true ||
      hook.settings?.is_homol === 'true'
        ? 'staging'
        : 'production';
    settings.typistCpf = hook.settings?.typist_cpf || '';
    settings.masterIndicator = hook.settings?.master_indicator || '';
    settings.apiKey =
      hook.settings?.api_key || hook.settings?.headers?.[0]?.value || '';
    settings.corbanHeaderName =
      hook.settings?.authorization_header_name ||
      hook.settings?.headers?.[0]?.key ||
      DEFAULT_CORBAN_HEADER_BY_PROVIDER[option.value] ||
      '';
    settings.headers = resolveCorbanHeaders(hook.settings || {}, option.value);
    settings.apiUrl = hook.settings?.api_url || '';
    applyCustomerServiceOrderSettings(hook.settings || {});
    applyCustomerServiceOpeningSettings(hook.settings || {});
    applyFinancialTrustUnlockSettings(hook.settings || {});
    applyInvoiceNotificationSettings(hook.settings || {});
    bankValidationState.provider = option.value;
    bankValidationState.valid =
      hook.settings?.connection_validated === true ||
      hook.settings?.connection_validated === 'true';
    Object.assign(
      settings.products,
      normalizeProducts(hook.settings?.products)
    );
  }

  providerDialogRef.value?.open();
};

const configureBank = provider => {
  const option = availableBankOptions.value.find(bank =>
    providerMatches(bank.value, provider)
  );
  if (option) openProviderSettings(option);
};

const requestBankStatusChange = provider => {
  if (isUpdatingBankStatus.value || uiFlags.value.isCreatingHook) return;
  const hook = getProviderHook(provider);
  if (!hook?.id) return;
  const enabled = !isHookEnabled(hook);
  if (
    enabled &&
    provider !== 'novo_saque' &&
    ![true, 'true'].includes(hook.settings?.connection_validated)
  ) {
    configureBank(provider);
    useAlert(localCopy.validationRequiredToEnable);
    return;
  }
  pendingBankStatus.value = {
    hookId: hook.id,
    provider,
    enabled,
    name: bankCardTitle(
      BANK_OPTIONS.find(bank => providerMatches(bank.value, provider))
    ),
  };
  bankStatusDialogRef.value?.open();
};

const confirmBankStatusChange = async () => {
  if (!pendingBankStatus.value || isUpdatingBankStatus.value) return;
  const { hookId, enabled } = pendingBankStatus.value;
  isUpdatingBankStatus.value = true;
  try {
    await store.dispatch('integrations/updateHook', {
      hookId,
      hookData: { hook: { status: enabled ? 'enabled' : 'disabled' } },
    });
    bankStatusDialogRef.value?.close();
    useAlert(enabled ? 'Integração ativada.' : 'Integração desativada.', {
      variant: 'success',
    });
  } catch (error) {
    useAlert(extractRequestErrorMessage(error), { variant: 'error' });
  } finally {
    isUpdatingBankStatus.value = false;
  }
};

watch(currentHook, () => {
  if (!usesProviderDialog.value) loadHook();
});

watch(productConfigHook, loadProductConfig, { immediate: true });

watch(
  () => settings.productsSelected.slice(),
  selectedProducts => {
    if (settings.provider !== 'facta') return;

    const requiredProducts = selectedProducts.filter(product =>
      FACTA_PRODUCTS_WITH_TABLE.includes(product)
    );

    if (!requiredProducts.length) {
      settings.factaActiveProductTab = 'FGTS';
      settings.factaTableCode = '';
      return;
    }

    if (!requiredProducts.includes(settings.factaActiveProductTab)) {
      settings.factaActiveProductTab = requiredProducts[0];
    }

    settings.factaTableCode =
      requiredProducts
        .map(product => settings.factaTableCodesByProduct[product])
        .find(Boolean) || '';
  }
);

onMounted(async () => {
  await Promise.all([
    store.dispatch('integrations/get'),
    store.dispatch('inboxes/getFromNetwork'),
  ]);
  if (!usesProviderDialog.value) loadHook();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching || !isLoaded"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="integrationConfig.title"
        :description="integrationConfig.description"
        feature-name="integrations"
      />
    </template>

    <template #body>
      <div class="flex-grow overflow-auto">
        <div class="max-w-[1180px] space-y-4">
          <section
            v-if="props.integrationId !== 'banking'"
            class="rounded-xl border border-n-weak bg-n-solid-2 p-5"
          >
            <div class="flex items-start gap-4">
              <div
                class="flex size-12 items-center justify-center rounded-lg border border-n-weak bg-n-alpha-3 text-n-blue-11"
              >
                <span :class="integrationConfig.icon" class="size-6" />
              </div>
              <div class="min-w-0 flex-1">
                <h3 class="text-heading-1 text-n-slate-12">
                  {{ integrationConfig.title }}
                </h3>
                <p class="mt-1 text-body-main text-n-slate-11">
                  {{ integrationConfig.description }}
                </p>
              </div>
            </div>
          </section>

          <section
            class="rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-sm"
          >
            <div
              v-if="props.integrationId === 'banking'"
              class="mb-6 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div
                class="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between"
              >
                <div class="flex min-w-0 items-center gap-4">
                  <span
                    class="grid size-12 shrink-0 place-content-center rounded-xl border border-n-weak bg-n-alpha-2 text-n-slate-11"
                  >
                    <span class="i-lucide-landmark size-6" />
                  </span>
                  <div class="min-w-0">
                    <p
                      class="text-xs font-semibold uppercase tracking-[0.14em] text-n-slate-10"
                    >
                      Central de integrações
                    </p>
                    <h3 class="mt-1 text-xl font-semibold text-n-slate-12">
                      Bancos conectados
                    </h3>
                    <p class="mt-1 max-w-xl text-sm text-n-slate-10">
                      Configure credenciais, produtos e a prioridade de consulta
                      em um só lugar.
                    </p>
                  </div>
                </div>

                <div class="grid grid-cols-3 gap-2 sm:min-w-[330px]">
                  <div
                    class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2.5"
                  >
                    <span class="block text-xl font-semibold text-n-slate-12">
                      {{ bankingStats.total }}
                    </span>
                    <span class="text-[11px] text-n-slate-10">Disponíveis</span>
                  </div>
                  <div
                    class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2.5"
                  >
                    <span class="block text-xl font-semibold text-n-slate-12">
                      {{ bankingStats.configured }}
                    </span>
                    <span class="text-[11px] text-n-slate-10">
                      {{ 'Configurados' }}
                    </span>
                  </div>
                  <div
                    class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2.5"
                  >
                    <span class="block text-xl font-semibold text-n-teal-11">
                      {{ bankingStats.active }}
                    </span>
                    <span class="text-[11px] text-n-slate-10">Ativos</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!usesProviderDialog"
              class="mb-5 flex items-center justify-between gap-4 rounded-lg border border-n-weak bg-n-solid-1 p-4"
            >
              <div>
                <h4 class="text-heading-3 text-n-slate-12">
                  {{ $t('INTEGRATION_APPS.LOCAL.ACTIVE_LABEL') }}
                </h4>
                <p class="text-body-small text-n-slate-11">
                  {{ $t('INTEGRATION_APPS.LOCAL.ACTIVE_DESCRIPTION') }}
                </p>
              </div>
              <Switch v-model="settings.enabled" />
            </div>

            <div class="space-y-4">
              <div class="flex flex-col gap-4">
                <BankIntegrationPicker
                  v-if="props.integrationId === 'banking'"
                  :banks="bankCards"
                  :busy="isUpdatingBankStatus || uiFlags.isCreatingHook"
                  @configure="configureBank"
                  @toggle="requestBankStatusChange"
                />
                <label v-else class="text-heading-3 text-n-slate-12">
                  {{ integrationConfig.providerLabel }}
                </label>
                <div
                  v-if="props.integrationId !== 'banking'"
                  class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                >
                  <button
                    v-for="option in displayedProviderOptions"
                    :key="option.value"
                    type="button"
                    class="group relative flex min-h-[138px] cursor-pointer flex-col justify-between gap-4 overflow-hidden rounded-xl border p-4 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-solid-2"
                    :class="providerCardClass(option.value)"
                    :aria-pressed="
                      (!usesProviderDialog &&
                        providerMatches(option.value, settings.provider)) ||
                      isProviderEnabled(option.value)
                    "
                    @click="openProviderSettings(option)"
                  >
                    <span class="flex items-start justify-between gap-3">
                      <span class="flex min-w-0 items-center gap-3">
                        <span
                          class="flex size-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold"
                          :class="providerBadgeClass(option.value)"
                        >
                          <img
                            v-if="providerLogos[option.value]"
                            :src="providerLogos[option.value]"
                            :alt="option.label"
                            class="size-9 object-contain"
                          />
                          <template v-else>{{
                            option.label.slice(0, 2).toUpperCase()
                          }}</template>
                        </span>
                        <span class="min-w-0">
                          <span
                            class="block truncate text-sm font-semibold leading-tight text-n-slate-12"
                          >
                            {{ bankCardTitle(option) || option.label }}
                          </span>
                          <span
                            class="mt-1.5 line-clamp-2 block text-xs leading-4 text-n-slate-10"
                          >
                            {{
                              BANK_CARD_META[option.value]?.subtitle ||
                              (props.integrationId === 'erp'
                                ? 'Clientes, cobranças e atendimento do seu provedor'
                                : 'Integração para consultas bancárias')
                            }}
                          </span>
                        </span>
                      </span>
                      <span class="flex shrink-0 items-center gap-2">
                        <span
                          v-if="BANK_CARD_META[option.value]?.beta"
                          class="rounded-full border border-n-blue-6/70 bg-n-blue-3 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-n-blue-11"
                        >
                          Beta
                        </span>
                        <span
                          class="i-lucide-chevron-right size-4 text-n-slate-10 transition-transform duration-150 group-hover:translate-x-0.5"
                        />
                      </span>
                    </span>

                    <span class="flex flex-wrap items-center gap-1.5">
                      <span
                        class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold"
                        :class="providerStatusPillClass(option.value)"
                      >
                        <span
                          class="size-1.5 rounded-full bg-current opacity-80"
                        />
                        {{
                          isProviderEnabled(option.value)
                            ? 'Ativo'
                            : isProviderConfigured(option.value)
                              ? 'Inativo'
                              : 'Não configurado'
                        }}
                      </span>
                      <span
                        v-if="
                          ['banking', 'corban'].includes(props.integrationId) &&
                          getProviderProduct(option.value)
                        "
                        class="inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-semibold"
                        :class="providerProductPillClass(option.value)"
                      >
                        {{ getProviderProduct(option.value) }}
                      </span>
                    </span>
                  </button>
                </div>
              </div>

              <div
                v-if="!usesProviderDialog"
                class="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div
                  v-if="settings.provider === 'novo_saque'"
                  class="space-y-3 md:col-span-2"
                >
                  <label class="block text-sm font-medium text-n-slate-12">
                    Ambiente
                    <ComboBox
                      v-model="bullEnvironment"
                      :options="[
                        { value: 'staging', label: 'Sandbox' },
                        { value: 'production', label: 'Produção' },
                      ]"
                      @update:model-value="settings.apiKey = ''"
                    />
                  </label>
                  <Input
                    v-if="isProductionEnvironment"
                    v-model="settings.apiUrl"
                    label="URL de Produção"
                    placeholder="URL HTTPS fornecida pelo Novo Saque"
                  />
                  <p v-else class="text-xs text-n-slate-11">
                    Sandbox: https://api.novosaque.dev.br
                  </p>
                  <p class="text-xs text-n-slate-11">
                    Informe a chave do ambiente selecionado. Ao trocar de
                    ambiente, preencha a chave correspondente.
                  </p>
                  <p
                    class="rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
                  >
                    Salve a API Key e ative a integração. O acesso será
                    verificado na primeira simulação. Se houver recusa de
                    autenticação (401 ou 403), confira a chave e o ambiente.
                  </p>
                </div>
                <Input
                  v-if="requiresApiUrl"
                  v-model="settings.apiUrl"
                  :label="$t('INTEGRATION_APPS.LOCAL.API_URL')"
                  placeholder="https://api.exemplo.com.br"
                  :message="erpApiUrlError"
                  :message-type="erpApiUrlError ? 'error' : 'info'"
                  @blur="normalizeErpApiUrlField"
                />

                <p
                  v-if="settings.provider === 'bank321'"
                  class="m-0 rounded-xl bg-n-amber-3 p-3 text-sm text-n-amber-11 md:col-span-2"
                >
                  321Bank — Consignado Trabalhador. Configure o ambiente, o CPF
                  de acesso, a senha, a API Key e o CPF certificado do
                  digitador.
                </p>
                <Input
                  v-if="
                    requiresCredentials &&
                    !usesClientCredentials &&
                    settings.provider !== 'neon'
                  "
                  v-model="settings.username"
                  :label="
                    settings.provider === 'bank321'
                      ? 'CPF de acesso'
                      : $t('INTEGRATION_APPS.LOCAL.USERNAME')
                  "
                  autocomplete="off"
                />

                <Input
                  v-if="
                    requiresCredentials &&
                    !usesClientCredentials &&
                    settings.provider !== 'neon'
                  "
                  v-model="settings.password"
                  type="password"
                  :label="
                    settings.provider === 'bank321'
                      ? 'Senha'
                      : $t('INTEGRATION_APPS.LOCAL.PASSWORD')
                  "
                  autocomplete="new-password"
                  :show-password-toggle="isAdmin"
                />

                <Input
                  v-if="settings.provider === 'neon'"
                  v-model="settings.neonOrigin"
                  label="x-origin"
                  placeholder="Origem/canal fornecido pelo Neon"
                  autocomplete="off"
                />

                <Input
                  v-if="settings.provider === 'neon'"
                  v-model="settings.neonTokenId"
                  type="password"
                  label="token-id"
                  placeholder="Token de autenticação"
                  autocomplete="new-password"
                  :show-password-toggle="isAdmin"
                />

                <Input
                  v-if="settings.provider === 'neon'"
                  v-model="settings.neonValidationCpf"
                  label="CPF para testar a conexão"
                  placeholder="Somente 11 números"
                  inputmode="numeric"
                  maxlength="14"
                  message="Usado apenas na validação; este CPF não será salvo."
                />

                <Input
                  v-if="
                    usesClientCredentials ||
                    (props.integrationId === 'banking' &&
                      settings.provider === 'v8')
                  "
                  v-model="settings.clientId"
                  label="Client ID"
                  autocomplete="off"
                />

                <Input
                  v-if="usesClientCredentials"
                  v-model="settings.clientSecret"
                  type="password"
                  label="Client Secret"
                  autocomplete="new-password"
                  :show-password-toggle="isAdmin"
                />

                <button
                  v-if="
                    [
                      'bull',
                      'neon',
                      'fintech_corban',
                      'bank321',
                      'anchor',
                    ].includes(settings.provider)
                  "
                  type="button"
                  class="group flex w-full items-center justify-between gap-4 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :class="
                    isProductionEnvironment
                      ? 'border-n-blue-7 bg-n-blue-3/20 hover:bg-n-blue-3/30 dark:border-n-blue-8 dark:bg-n-blue-9/10 dark:ring-1 dark:ring-n-blue-8/30 dark:hover:bg-n-blue-9/15'
                      : 'border-n-weak bg-n-alpha-1 hover:border-n-strong hover:bg-n-alpha-2'
                  "
                  :aria-pressed="isProductionEnvironment"
                  @click="isProductionEnvironment = !isProductionEnvironment"
                >
                  <div class="flex min-w-0 items-center gap-3">
                    <span
                      class="grid size-9 shrink-0 place-content-center rounded-lg"
                      :class="
                        isProductionEnvironment
                          ? 'bg-n-blue-4 text-n-blue-11 dark:bg-n-blue-9/20 dark:text-n-blue-9'
                          : 'bg-n-alpha-2 text-n-slate-11'
                      "
                    >
                      <span
                        class="size-4"
                        :class="
                          isProductionEnvironment
                            ? 'i-lucide-cloud'
                            : 'i-lucide-flask-conical'
                        "
                      />
                    </span>
                    <div class="min-w-0">
                      <p class="text-sm font-medium text-n-slate-12">
                        {{
                          isProductionEnvironment ? 'Produção' : 'Homologação'
                        }}
                      </p>
                      <p
                        class="mt-0.5 text-xs text-n-slate-10 dark:text-n-slate-11"
                      >
                        Clique para usar
                        {{
                          isProductionEnvironment ? 'homologação' : 'produção'
                        }}
                      </p>
                    </div>
                  </div>
                  <span
                    class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold"
                    :class="
                      isProductionEnvironment
                        ? 'bg-n-blue-4 text-n-blue-11 dark:bg-n-blue-9/20 dark:text-n-blue-9'
                        : 'bg-n-alpha-2 text-n-slate-11'
                    "
                  >
                    <span class="size-1.5 rounded-full bg-current" />
                    {{
                      isProductionEnvironment
                        ? 'Produção ativa'
                        : 'Homologação ativa'
                    }}
                  </span>
                </button>

                <div
                  v-if="supportsAdditionalCredentialFields"
                  class="flex min-w-0 items-end gap-2"
                >
                  <Input
                    v-model="settings.typistCpf"
                    class="min-w-0 flex-1"
                    :label="additionalCredentialLabel"
                    :placeholder="additionalCredentialPlaceholder"
                  />
                  <Button
                    v-if="settings.provider === 'c6'"
                    type="button"
                    icon="i-lucide-search"
                    :label="localCopy.c6TypistCodeConsult"
                    :is-loading="isConsultingC6TypistCode"
                    :disabled="isConsultingC6TypistCode"
                    @click="consultC6TypistCode"
                  />
                </div>

                <Input
                  v-if="showLegacyMasterIndicatorField"
                  v-model="settings.masterIndicator"
                  label="Indicador Master"
                  placeholder="Ex.: 54531"
                />

                <div
                  v-if="shouldShowApiKeyField"
                  :class="
                    props.integrationId === 'corban' ? 'md:col-span-2' : ''
                  "
                >
                  <Input
                    v-model="settings.apiKey"
                    type="password"
                    :label="apiKeyLabel"
                    autocomplete="new-password"
                    :show-password-toggle="isAdmin"
                    :message="
                      settings.provider === 'ixcsoft'
                        ? localCopy.ixcApiKeyHint
                        : ''
                    "
                    @blur="normalizeIxcApiKeyField"
                  />
                </div>

                <Input
                  v-if="props.integrationId === 'corban'"
                  v-model="settings.corbanHeaderName"
                  :label="localCopy.customAuthorizationHeader"
                  placeholder="Authorization"
                />
              </div>
            </div>

            <div class="flex flex-wrap items-center justify-end gap-3 pt-1">
              <Button
                v-if="showIxcInvoiceReport"
                :label="localCopy.invoiceReport"
                icon="i-lucide-file-bar-chart"
                slate
                faded
                @click="
                  router.push({
                    name: 'ixc_invoice_notification_reports',
                    params: { accountId: route.params.accountId },
                  })
                "
              />
              <Button
                v-if="props.integrationId !== 'banking'"
                :label="$t('INTEGRATION_APPS.LOCAL.BACK')"
                slate
                faded
                @click="goBack"
              />
              <Button
                v-if="!usesProviderDialog"
                :label="$t('INTEGRATION_APPS.LOCAL.VALIDATE_CONNECTION')"
                icon="i-lucide-check-circle"
                teal
                faded
                :is-loading="isValidatingConnection"
                :disabled="!isFormValid || isValidatingConnection"
                @click="validateConnection"
              />
              <Button
                v-if="!usesProviderDialog"
                :label="$t('INTEGRATION_APPS.LOCAL.SAVE')"
                icon="i-lucide-save"
                solid
                blue
                :is-loading="uiFlags.isCreatingHook"
                :disabled="!isFormValid || uiFlags.isCreatingHook"
                @click="saveSettings"
              />
            </div>
          </section>

          <!-- Product-bank configuration section -->
          <section
            v-if="props.integrationId === 'banking' && productBanksView.length"
            class="rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-sm"
          >
            <div
              class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
            >
              <div class="flex items-start gap-3">
                <span
                  class="grid size-10 shrink-0 place-content-center rounded-xl border border-n-weak bg-n-alpha-2 text-n-slate-11"
                >
                  <span class="i-lucide-list-ordered size-5" />
                </span>
                <div>
                  <p
                    class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
                  >
                    Produtos e prioridade
                  </p>
                  <h3 class="mt-1 text-base font-semibold text-n-slate-12">
                    {{ localCopy.productConfig }}
                  </h3>
                  <p class="mt-1 max-w-2xl text-xs leading-5 text-n-slate-10">
                    Arraste os bancos para definir a sequência de consulta. O
                    primeiro da lista tem maior prioridade.
                  </p>
                </div>
              </div>
              <Button
                :label="localCopy.saveProductConfig"
                icon="i-lucide-save"
                solid
                blue
                class="shadow-md shadow-n-brand/15 ring-1 ring-n-brand/30"
                :is-loading="isSavingProductConfig"
                :disabled="isSavingProductConfig"
                @click="saveProductConfig"
              />
            </div>

            <section
              class="mt-4 space-y-3 rounded-2xl border border-n-weak bg-n-solid-1 p-4"
            >
              <header>
                <h4 class="text-sm font-semibold text-n-slate-12">
                  Oferta de outro produto (cross-sell)
                </h4>
                <p class="mt-1 text-xs text-n-slate-11">
                  Sugira uma alternativa quando todos os bancos consultados
                  concluírem sem oferta.
                </p>
              </header>
              <div class="grid gap-3 md:grid-cols-2">
                <div
                  class="flex items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                >
                  <div>
                    <p
                      id="cross-sell-toggle-label"
                      class="text-sm font-medium text-n-slate-12"
                    >
                      Sugerir outro produto
                    </p>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{
                        bankConfig.crossSell.enabled
                          ? 'Habilitado nesta configuração'
                          : 'Desabilitado nesta configuração'
                      }}
                    </p>
                  </div>
                  <Switch
                    v-model="bankConfig.crossSell.enabled"
                    :disabled="
                      !crossSellAvailable && !bankConfig.crossSell.enabled
                    "
                    aria-labelledby="cross-sell-toggle-label"
                  />
                </div>
                <div
                  class="space-y-2 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                >
                  <p
                    id="cross-sell-order-label"
                    class="text-xs font-medium text-n-slate-11"
                  >
                    Ordem dos produtos
                  </p>
                  <ComboBox
                    v-model="bankConfig.crossSell.primary"
                    :options="[
                      { value: 'CLT', label: '1º CLT → 2º FGTS' },
                      { value: 'FGTS', label: '1º FGTS → 2º CLT' },
                    ]"
                    aria-labelledby="cross-sell-order-label"
                    search-placeholder="Buscar ordem dos produtos"
                    placeholder="Selecione a ordem"
                    teleport-to-body
                  />
                </div>
              </div>
              <p class="text-xs text-n-slate-11">
                É necessário ter pelo menos uma integração validada e ativa com
                CLT e FGTS habilitados no mesmo banco: Hand+, Facta ou V8. A
                ordem dos bancos continua sendo definida abaixo.
              </p>
              <p v-if="!crossSellAvailable" class="text-xs text-n-amber-11">
                Indisponível: habilite os dois produtos em uma integração ativa
                e validada.
              </p>
              <p class="text-xs text-n-slate-11">
                A regra orienta o operador. Não envia mensagens, não consulta
                outro produto automaticamente e não substitui a autorização
                específica do cliente.
              </p>
            </section>

            <div class="mt-4 grid gap-3 sm:grid-cols-3">
              <div
                v-for="(step, stepIndex) in [
                  ['Defina o alcance', 'Conta inteira ou uma caixa', 'scope'],
                  ['Escolha o produto', 'CLT ou FGTS', 'product'],
                  ['Organize os bancos', 'Arraste pela prioridade', 'order'],
                ]"
                :key="step[2]"
                class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5"
              >
                <span
                  class="grid size-7 shrink-0 place-content-center rounded-full bg-n-brand/10 text-xs font-bold text-n-brand"
                >
                  {{ stepIndex + 1 }}
                </span>
                <span class="min-w-0">
                  <strong class="block text-xs text-n-slate-12">
                    {{ step[0] }}
                  </strong>
                  <small class="mt-0.5 block text-[11px] text-n-slate-10">
                    {{ step[1] }}
                  </small>
                </span>
              </div>
            </div>

            <section
              class="mt-4 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1"
            >
              <header
                class="flex flex-col gap-2 border-b border-n-weak bg-n-alpha-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    Onde esta sequência será usada?
                  </h4>
                  <p class="mt-0.5 text-xs text-n-slate-10">
                    Crie uma ordem geral ou personalize o atendimento de cada
                    caixa.
                  </p>
                </div>
                <span
                  class="inline-flex w-max items-center gap-1.5 rounded-full bg-n-brand/10 px-2.5 py-1 text-[11px] font-semibold text-n-brand"
                >
                  <span
                    :class="activeBankOrderScopeOption.icon"
                    class="size-3.5"
                  />
                  {{ activeBankOrderScopeOption.label }}
                </span>
              </header>

              <div
                class="grid gap-3 p-3 lg:grid-cols-[minmax(220px,0.7fr)_minmax(320px,1.3fr)]"
              >
                <button
                  type="button"
                  class="group flex min-w-0 items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all"
                  :class="
                    activeBankOrderScope === 'default'
                      ? 'border-n-brand bg-n-brand/5 shadow-sm ring-1 ring-n-brand/20'
                      : 'border-n-weak bg-n-solid-1 hover:-translate-y-px hover:border-n-strong hover:shadow-sm'
                  "
                  @click="activeBankOrderScope = 'default'"
                >
                  <span
                    class="grid size-9 shrink-0 place-content-center rounded-lg transition-colors"
                    :class="
                      activeBankOrderScope === 'default'
                        ? 'bg-n-brand text-white'
                        : 'bg-n-alpha-2 text-n-slate-10 group-hover:text-n-brand'
                    "
                  >
                    <span class="i-lucide-building-2 size-4" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <small
                      class="block truncate text-[10px] font-semibold uppercase tracking-wide text-n-slate-9"
                    >
                      Ordem usada como padrão
                    </small>
                    <strong
                      class="mt-0.5 block truncate text-xs text-n-slate-12"
                    >
                      Conta inteira
                    </strong>
                  </span>
                  <span
                    v-if="activeBankOrderScope === 'default'"
                    class="i-lucide-circle-check size-4 shrink-0 text-n-brand"
                  />
                </button>

                <div
                  class="flex min-w-0 items-center gap-3 rounded-xl border px-3 py-3 transition-all"
                  :class="
                    activeBankOrderInboxId
                      ? 'border-n-brand bg-n-brand/5 shadow-sm ring-1 ring-n-brand/20'
                      : 'border-n-weak bg-n-solid-1'
                  "
                >
                  <span
                    class="grid size-9 shrink-0 place-content-center rounded-lg"
                    :class="
                      activeBankOrderInboxId
                        ? 'bg-n-brand text-white'
                        : 'bg-n-alpha-2 text-n-slate-10'
                    "
                  >
                    <span class="i-lucide-inbox size-4" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <small
                      class="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-n-slate-9"
                    >
                      Personalizar uma caixa
                    </small>
                    <ComboBox
                      v-model="bankOrderInboxScopeValue"
                      :options="bankOrderInboxScopeOptions"
                      placeholder="Selecione uma caixa de entrada"
                      search-placeholder="Buscar caixa pelo nome..."
                      empty-state="Nenhuma caixa encontrada."
                      teleport-to-body
                    />
                  </div>
                </div>
              </div>

              <p
                class="border-t border-n-weak bg-n-alpha-1 px-4 py-2.5 text-[11px] leading-5 text-n-slate-10"
              >
                A ordem da conta inteira é aplicada automaticamente às caixas
                sem personalização. Ao selecionar uma caixa, as alterações ficam
                exclusivas para ela.
              </p>
            </section>

            <div
              class="mt-4 flex w-max gap-1 overflow-x-auto rounded-lg border border-n-weak bg-n-alpha-2 p-1"
            >
              <button
                v-for="productConfig in productBanksView"
                :key="productConfig.value"
                type="button"
                class="inline-flex h-8 shrink-0 items-center gap-2 rounded-md px-4 text-xs font-semibold transition"
                :class="
                  activeBankProductTab === productConfig.value
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm ring-1 ring-n-strong'
                    : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12'
                "
                @click="activeBankProductTab = productConfig.value"
              >
                {{ productConfig.label }}
                <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px]">
                  {{ productConfig.hooks.length }}
                </span>
              </button>
            </div>

            <div v-if="activeProductConfig" class="mt-4 grid gap-3">
              <div
                class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
              >
                <span class="flex items-center gap-2 text-xs text-n-slate-11">
                  <span
                    :class="
                      draggedBank
                        ? 'i-lucide-move animate-pulse'
                        : 'i-lucide-grip-vertical'
                    "
                    class="size-4"
                  />
                  {{
                    draggedBank
                      ? 'A lista já está assumindo a nova posição'
                      : 'Segure e arraste; os bancos se reorganizam em tempo real'
                  }}
                </span>
                <span class="text-xs font-medium text-n-slate-10">
                  {{ activeProductConfig.hooks.length }}
                  {{
                    activeProductConfig.hooks.length === 1 ? 'banco' : 'bancos'
                  }}
                </span>
              </div>
              <div
                class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1"
              >
                <div
                  class="grid grid-cols-[32px_40px_minmax(0,1fr)_90px_240px_72px] gap-3 border-b border-n-weak bg-n-solid-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 max-lg:hidden"
                >
                  <span />
                  <span>#</span>
                  <span>Banco</span>
                  <span>Conexão</span>
                  <span>Ações</span>
                  <span>Ordem</span>
                </div>
                <TransitionGroup
                  name="bank-order"
                  tag="div"
                  class="space-y-2 p-2"
                >
                  <div
                    v-for="(hook, bankIndex) in activeProductConfig.hooks"
                    :key="hook.id"
                    draggable="true"
                    class="group relative grid grid-cols-[28px_minmax(0,1fr)] cursor-grab gap-2 rounded-lg border px-3 py-2 transition-all duration-200 active:cursor-grabbing lg:grid-cols-[32px_40px_minmax(0,1fr)_90px_240px_72px] lg:items-center"
                    :class="[
                      isHookEnabled(hook)
                        ? 'border-n-strong bg-n-solid-1'
                        : 'border-n-weak bg-n-solid-2',
                      draggedBank?.hookId === String(hook.id)
                        ? 'z-10 scale-[1.01] opacity-70 shadow-lg ring-1 ring-n-brand'
                        : 'hover:-translate-y-px hover:border-n-strong hover:shadow-sm',
                    ]"
                    @dragstart="
                      startBankDrag(activeProductConfig.value, hook.id, $event)
                    "
                    @dragenter.prevent="
                      previewBankPosition(activeProductConfig.value, hook.id)
                    "
                    @dragover.prevent
                    @drop.prevent="dropBank"
                    @dragend="endBankDrag"
                  >
                    <span
                      class="hidden size-8 items-center justify-center rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-9 transition group-hover:text-n-blue-10 lg:flex"
                    >
                      <span class="i-lucide-grip-vertical size-4" />
                    </span>
                    <span
                      class="flex size-6 items-center justify-center rounded-md text-xs font-bold"
                      :class="
                        isHookEnabled(hook)
                          ? 'bg-n-alpha-3 text-n-slate-12'
                          : 'bg-n-alpha-2 text-n-slate-11'
                      "
                    >
                      {{ bankIndex + 1 }}
                    </span>
                    <div class="flex min-w-0 items-center gap-2">
                      <BankLogo :provider="hook.settings?.provider" />
                      <div class="min-w-0">
                        <strong class="block text-sm text-n-slate-12">
                          {{
                            BANK_OPTIONS.find(
                              b => b.value === hook.settings?.provider
                            )?.label || hook.settings?.provider
                          }}
                        </strong>
                        <span class="mt-1 block text-xs text-n-slate-10">
                          Produtos:
                          {{
                            getProviderProduct(hook.settings?.provider) ||
                            activeProductConfig.label
                          }}
                        </span>
                      </div>
                    </div>
                    <span
                      class="w-max rounded-full border px-2 py-0.5 text-[11px] font-semibold max-lg:col-start-2"
                      :class="
                        isHookEnabled(hook)
                          ? 'border-n-teal-7 bg-n-teal-3/20 text-n-teal-10'
                          : 'border-n-weak bg-n-alpha-1 text-n-slate-9'
                      "
                    >
                      {{
                        isHookEnabled(hook)
                          ? $t('INTEGRATION_APPS.LOCAL.STATUS_ENABLED')
                          : $t('INTEGRATION_APPS.LOCAL.STATUS_DISABLED')
                      }}
                    </span>
                    <div
                      class="flex flex-wrap items-center gap-1.5 max-lg:col-start-2"
                      @dragstart.stop.prevent
                    >
                      <label
                        :for="`bank-order-status-${hook.id}`"
                        class="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-n-slate-11"
                      >
                        <Switch
                          :id="`bank-order-status-${hook.id}`"
                          :model-value="isHookEnabled(hook)"
                          :aria-label="`${isHookEnabled(hook) ? 'Desativar' : 'Ativar'} ${hook.settings?.provider}`"
                          :disabled="
                            isUpdatingBankStatus || uiFlags.isCreatingHook
                          "
                          @update:model-value="
                            requestBankStatusChange(hook.settings?.provider)
                          "
                        />
                        <span>{{
                          isHookEnabled(hook) ? 'Desativar' : 'Ativar'
                        }}</span>
                      </label>
                      <Button
                        label="Configurar"
                        icon="i-lucide-settings-2"
                        color="slate"
                        variant="ghost"
                        size="sm"
                        :disabled="
                          isUpdatingBankStatus || uiFlags.isCreatingHook
                        "
                        @click="configureBank(hook.settings?.provider)"
                      />
                    </div>
                    <div class="flex gap-1 max-lg:col-start-2 lg:justify-end">
                      <button
                        type="button"
                        class="grid size-7 place-content-center rounded-md border border-n-weak text-n-slate-10 transition hover:border-n-blue-8 hover:text-n-blue-10 disabled:cursor-not-allowed disabled:opacity-30"
                        :disabled="bankIndex === 0"
                        title="Subir prioridade"
                        @click="moveBankUp(activeProductConfig.value, hook.id)"
                      >
                        <span class="i-lucide-chevron-up size-4" />
                      </button>
                      <button
                        type="button"
                        class="grid size-7 place-content-center rounded-md border border-n-weak text-n-slate-10 transition hover:border-n-blue-8 hover:text-n-blue-10 disabled:cursor-not-allowed disabled:opacity-30"
                        :disabled="
                          bankIndex === activeProductConfig.hooks.length - 1
                        "
                        title="Descer prioridade"
                        @click="
                          moveBankDown(activeProductConfig.value, hook.id)
                        "
                      >
                        <span class="i-lucide-chevron-down size-4" />
                      </button>
                    </div>
                  </div>
                </TransitionGroup>
              </div>

              <section
                class="rounded-2xl border border-n-weak bg-n-solid-1 p-4"
              >
                <div
                  class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div class="flex min-w-0 items-start gap-3">
                    <span
                      class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-brand/10 text-n-brand"
                    >
                      <span class="i-lucide-flag size-4" />
                    </span>
                    <div>
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        Prioridade por flag da mensagem
                      </h4>
                      <p
                        class="mt-1 max-w-2xl text-xs leading-5 text-n-slate-10"
                      >
                        Quando uma mensagem recebida contiver a flag, o banco
                        escolhido será consultado primeiro. A comparação ignora
                        maiúsculas e minúsculas e não altera a ordem salva.
                      </p>
                    </div>
                  </div>
                  <Button
                    label="Nova regra"
                    icon="i-lucide-plus"
                    slate
                    faded
                    size="sm"
                    class="shrink-0"
                    @click="addPriorityFlag"
                  />
                </div>

                <div
                  v-if="!bankConfig.priorityFlags.length"
                  class="mt-4 rounded-xl border border-dashed border-n-weak px-4 py-5 text-center"
                >
                  <p class="text-sm font-medium text-n-slate-11">
                    Nenhuma prioridade por mensagem configurada
                  </p>
                  <p class="mt-1 text-xs text-n-slate-10">
                    Exemplo: use a flag (c6) para consultar o C6 primeiro.
                  </p>
                </div>

                <div v-else class="mt-4 space-y-3">
                  <div
                    v-for="(rule, ruleIndex) in bankConfig.priorityFlags"
                    :key="ruleIndex"
                    class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 lg:grid-cols-[minmax(140px,1fr)_150px_minmax(180px,1fr)_minmax(180px,1fr)_36px] lg:items-end"
                  >
                    <Input
                      v-model="rule.flag"
                      label="Flag da mensagem"
                      placeholder="Ex.: (c6)"
                    />
                    <ComboBox
                      v-model="rule.product"
                      label="Produto"
                      :options="BANK_PRODUCT_OPTIONS"
                      @update:model-value="rule.hook_id = ''"
                    />
                    <ComboBox
                      v-model="rule.hook_id"
                      label="Banco prioritário"
                      :options="priorityFlagBankOptions(rule.product)"
                    />
                    <ComboBox
                      v-model="rule.inbox_id"
                      label="Caixa de entrada"
                      :options="priorityFlagInboxOptions"
                    />
                    <button
                      type="button"
                      class="grid size-9 place-content-center rounded-lg border border-n-weak text-n-slate-10 transition hover:border-n-ruby-7 hover:bg-n-ruby-3 hover:text-n-ruby-11"
                      title="Remover regra"
                      @click="removePriorityFlag(ruleIndex)"
                    >
                      <span class="i-lucide-trash-2 size-4" />
                    </button>
                  </div>
                </div>
              </section>

              <aside v-if="false" class="grid gap-3 md:grid-cols-2">
                <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3">
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        {{ localCopy.followupAuto }}
                      </h4>
                      <p class="mt-1 text-xs leading-5 text-n-slate-10">
                        {{ localCopy.followupDescription }}
                      </p>
                    </div>
                    <Switch
                      v-model="
                        bankConfig.productFollowup[activeProductConfig.value]
                          .enabled
                      "
                    />
                  </div>
                  <div
                    v-if="
                      bankConfig.productFollowup[activeProductConfig.value]
                        .enabled
                    "
                    class="mt-3 space-y-2"
                  >
                    <p
                      v-if="
                        !bankConfig.productFollowup[activeProductConfig.value]
                          .items.length
                      "
                      class="rounded-lg border border-dashed border-n-weak px-3 py-2 text-xs text-n-slate-10"
                    >
                      {{ localCopy.followupEmpty }}
                    </p>
                    <div
                      v-for="(item, itemIdx) in bankConfig.productFollowup[
                        activeProductConfig.value
                      ].items"
                      :key="itemIdx"
                      class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
                    >
                      <div class="mb-2 flex items-center justify-between gap-2">
                        <span class="text-xs font-semibold text-n-slate-11">
                          {{ localCopy.followupItem }} {{ itemIdx + 1 }}
                        </span>
                        <button
                          type="button"
                          class="grid size-7 place-content-center rounded-lg text-n-slate-10 hover:bg-n-ruby-3 hover:text-n-ruby-11"
                          @click="
                            removeFollowup(activeProductConfig.value, itemIdx)
                          "
                        >
                          <span class="i-lucide-x size-4" />
                        </button>
                      </div>
                      <div
                        class="mb-3 grid grid-cols-[minmax(0,1fr)_128px] gap-2"
                      >
                        <Input
                          v-model.number="item.delay_value"
                          type="number"
                          min="1"
                          size="sm"
                        />
                        <ComboBox
                          v-model="item.delay_unit"
                          :options="delayUnitOptions"
                        />
                      </div>
                      <TextArea
                        v-model="item.message"
                        :label="localCopy.followupMessage"
                        :placeholder="localCopy.followupPlaceholder"
                        min-height="4rem"
                        max-height="8rem"
                        auto-height
                      />
                    </div>
                    <button
                      type="button"
                      class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak py-2.5 text-sm text-n-slate-11 transition hover:border-n-blue-8 hover:text-n-blue-10"
                      @click="addFollowup(activeProductConfig.value)"
                    >
                      <span class="i-lucide-plus size-4" />
                      {{ localCopy.followupAdd }}
                    </button>
                  </div>
                </div>

                <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3">
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        {{ localCopy.offerSending }}
                      </h4>
                      <p class="mt-1 text-xs leading-5 text-n-slate-10">
                        {{ localCopy.offerSendingDescription }}
                      </p>
                    </div>
                    <Switch
                      v-model="
                        bankConfig.offerSending[activeProductConfig.value]
                          .enabled
                      "
                    />
                  </div>
                  <div
                    v-if="
                      bankConfig.offerSending[activeProductConfig.value].enabled
                    "
                    class="mt-4 grid gap-3"
                  >
                    <div class="grid grid-cols-[minmax(0,1fr)_128px] gap-2">
                      <Input
                        v-model.number="
                          bankConfig.offerSending[activeProductConfig.value]
                            .delay_value
                        "
                        type="number"
                        min="1"
                        size="sm"
                      />
                      <ComboBox
                        v-model="
                          bankConfig.offerSending[activeProductConfig.value]
                            .delay_unit
                        "
                        :options="delayUnitOptions"
                      />
                    </div>
                    <ComboBox
                      v-model="
                        bankConfig.offerSending[activeProductConfig.value].mode
                      "
                      :options="offerModeOptions"
                    />
                    <Input
                      v-if="
                        bankConfig.offerSending[activeProductConfig.value]
                          .mode === 'manual'
                      "
                      v-model="
                        bankConfig.offerSending[activeProductConfig.value]
                          .table_code
                      "
                      :label="localCopy.offerTable"
                      :placeholder="localCopy.offerTablePlaceholder"
                    />
                    <TextArea
                      v-model="
                        bankConfig.offerSending[activeProductConfig.value]
                          .message
                      "
                      :label="localCopy.offerMessage"
                      :placeholder="localCopy.offerMessagePlaceholder"
                      min-height="4rem"
                      max-height="8rem"
                      auto-height
                    />
                  </div>
                </div>
              </aside>

              <div
                class="mt-1 flex flex-col gap-3 rounded-xl border border-n-brand/25 bg-n-brand/5 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-n-slate-12">
                    Salvar ordem e prioridade
                  </p>
                  <p class="mt-0.5 text-xs text-n-slate-10">
                    Confirme as alterações feitas na lista de bancos.
                  </p>
                </div>
                <Button
                  :label="localCopy.saveProductConfig"
                  icon="i-lucide-save"
                  solid
                  blue
                  class="w-full shrink-0 shadow-md shadow-n-brand/15 ring-1 ring-n-brand/30 sm:w-auto"
                  :is-loading="isSavingProductConfig"
                  :disabled="isSavingProductConfig"
                  @click="saveProductConfig"
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      <Dialog
        ref="bankStatusDialogRef"
        :title="`${pendingBankStatus?.enabled ? 'Ativar' : 'Desativar'} ${pendingBankStatus?.name || 'integração'}?`"
        description="Esta alteração vale para toda a conta, inclusive quando a ordem está personalizada por caixa. As credenciais, os produtos e a prioridade serão preservados."
        :confirm-button-label="
          pendingBankStatus?.enabled
            ? 'Ativar integração'
            : 'Desativar integração'
        "
        :is-loading="isUpdatingBankStatus"
        :disable-confirm-button="isUpdatingBankStatus"
        :close-on-click-outside="!isUpdatingBankStatus"
        @confirm="confirmBankStatusChange"
      />
      <Dialog
        ref="providerDialogRef"
        :width="providerDialogWidth"
        overflow-y-auto
        position="top"
        :show-cancel-button="false"
        :show-confirm-button="false"
      >
        <div class="space-y-4">
          <header
            class="-mx-6 -mt-6 flex items-center justify-between gap-4 border-b border-n-weak bg-n-solid-2 px-6 py-5"
          >
            <div class="flex min-w-0 items-center gap-3">
              <span
                class="grid size-11 shrink-0 place-content-center rounded-xl border"
                :class="providerStatusClass"
              >
                <BankLogo
                  v-if="props.integrationId === 'banking'"
                  :provider="settings.provider"
                />
                <span v-else :class="integrationConfig.icon" class="size-5" />
              </span>
              <div class="min-w-0">
                <p class="text-xs font-medium text-n-slate-10">
                  Configurar integração
                </p>
                <div class="flex items-center gap-2">
                  <h3 class="truncate text-base font-semibold text-n-slate-12">
                    {{ selectedProviderOption?.label || providerDialogTitle }}
                  </h3>
                  <span
                    v-if="BANK_CARD_META[settings.provider]?.beta"
                    class="shrink-0 rounded-full border border-n-blue-6/70 bg-n-blue-3 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-n-blue-11"
                  >
                    Beta
                  </span>
                </div>
                <p class="mt-0.5 truncate text-xs text-n-slate-10">
                  {{
                    BANK_CARD_META[settings.provider]?.subtitle ||
                    'Credenciais e opções desta integração.'
                  }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
              aria-label="Fechar"
              @click="providerDialogRef.close()"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <div
            :class="
              props.integrationId === 'corban'
                ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)] gap-4 items-start'
                : 'space-y-4'
            "
          >
            <div class="space-y-4">
              <div
                class="grid grid-cols-1 gap-3 md:grid-cols-2"
                :class="
                  props.integrationId === 'banking'
                    ? 'rounded-xl border border-n-weak bg-n-solid-2 p-4'
                    : ''
                "
              >
                <section
                  v-if="props.integrationId === 'banking'"
                  class="border-b border-n-weak pb-4 md:col-span-2"
                >
                  <div class="mb-3 flex items-center justify-between gap-3">
                    <div class="flex items-center gap-2.5">
                      <span
                        class="grid size-8 place-content-center rounded-lg bg-n-teal-3 text-n-teal-10"
                      >
                        <span class="i-lucide-boxes size-4" />
                      </span>
                      <div>
                        <h4 class="text-sm font-semibold text-n-slate-12">
                          Produtos
                        </h4>
                        <p class="text-[11px] text-n-slate-10">
                          Defina quais consultas este banco poderá realizar.
                        </p>
                      </div>
                    </div>
                    <span class="text-xs text-n-slate-10">
                      Selecione ao menos um
                    </span>
                  </div>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      v-for="product in BANK_PRODUCT_OPTIONS.filter(
                        option =>
                          option.value === 'CLT' ||
                          ['handplus', 'facta', 'v8', 'novo_saque'].includes(
                            settings.provider
                          )
                      )"
                      :key="product.value"
                      type="button"
                      class="flex min-h-12 items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                      :class="
                        settings.productsSelected.includes(product.value)
                          ? 'border-n-teal-7 bg-n-alpha-1 text-n-slate-12'
                          : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:border-n-strong hover:bg-n-alpha-2'
                      "
                      @click="toggleBankProduct(product.value)"
                    >
                      <span>
                        <span class="block text-sm font-semibold">
                          {{ product.label }}
                        </span>
                      </span>
                      <span
                        class="grid size-5 shrink-0 place-content-center rounded-full border"
                        :class="
                          settings.productsSelected.includes(product.value)
                            ? 'border-n-teal-8 bg-transparent text-n-teal-10'
                            : 'border-n-slate-7 text-transparent'
                        "
                      >
                        <span class="i-lucide-check size-3" />
                      </span>
                    </button>
                  </div>
                </section>

                <div
                  v-if="requiresFactaTable"
                  class="flex flex-col gap-2 text-sm text-n-slate-12 md:col-span-2"
                >
                  <span class="font-medium">{{
                    localCopy.factaTableProducts
                  }}</span>
                  <div
                    class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-alpha-2 p-2"
                  >
                    <div class="flex flex-wrap items-center gap-2">
                      <button
                        v-for="product in factaProductsRequiringTable"
                        :key="`facta-tab-${product}`"
                        type="button"
                        class="inline-flex h-8 items-center gap-2 rounded-md border px-3 text-xs font-semibold transition-colors"
                        :class="
                          activeFactaTableProduct === product
                            ? 'border-n-teal-7 bg-n-teal-3/20 text-n-teal-10'
                            : 'border-n-weak bg-n-solid-1 text-n-slate-12 hover:border-n-blue-8'
                        "
                        @click="settings.factaActiveProductTab = product"
                      >
                        <span>{{ product }}</span>
                        <span
                          v-if="settings.factaTableCodesByProduct[product]"
                          class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-medium"
                        >
                          {{ settings.factaTableCodesByProduct[product] }}
                        </span>
                      </button>
                    </div>
                    <button
                      type="button"
                      class="inline-flex h-8 items-center gap-2 rounded-md border border-n-slate-7 bg-n-solid-1 px-3 text-xs font-semibold text-n-slate-12 transition-colors hover:border-n-blue-8 hover:text-n-blue-10 disabled:cursor-not-allowed disabled:opacity-60"
                      :disabled="
                        isConsultingFactaTables || isValidatingConnection
                      "
                      @click="consultFactaTables"
                    >
                      <span
                        v-if="isConsultingFactaTables"
                        class="i-lucide-loader-2 size-3.5 animate-spin"
                      />
                      <span>{{ localCopy.factaTableConsult }}</span>
                    </button>
                  </div>

                  <label class="font-medium">{{
                    localCopy.factaTableLabel
                  }}</label>
                  <ComboBox
                    v-model="factaTableInputValue"
                    class="w-full"
                    :options="factaTableOptions"
                    :placeholder="localCopy.factaTablePlaceholder"
                    :search-placeholder="localCopy.factaTableSearchPlaceholder"
                    :empty-state="localCopy.factaTableEmpty"
                  />
                  <span
                    v-if="!factaTableOptions.length"
                    class="text-xs text-n-slate-11"
                  >
                    {{ localCopy.factaTableEmpty }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ localCopy.factaTableRequiredProductsHint }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ localCopy.factaTableHint }}
                  </span>
                </div>

                <div
                  v-if="props.integrationId === 'banking'"
                  class="flex items-center gap-2.5 pt-1 md:col-span-2"
                >
                  <span
                    class="grid size-8 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                  >
                    <span class="i-lucide-key-round size-4" />
                  </span>
                  <div>
                    <h4 class="text-sm font-semibold text-n-slate-12">
                      Credenciais de acesso
                    </h4>
                    <p class="text-[11px] text-n-slate-10">
                      Os dados são armazenados de forma segura.
                    </p>
                  </div>
                </div>

                <div
                  v-if="settings.provider === 'novo_saque'"
                  class="space-y-3 md:col-span-2"
                >
                  <label class="block text-sm font-medium text-n-slate-12">
                    Ambiente
                    <ComboBox
                      v-model="bullEnvironment"
                      :options="[
                        { value: 'staging', label: 'Sandbox' },
                        { value: 'production', label: 'Produção' },
                      ]"
                      @update:model-value="settings.apiKey = ''"
                    />
                  </label>
                  <Input
                    v-if="isProductionEnvironment"
                    v-model="settings.apiUrl"
                    label="URL de Produção"
                    placeholder="URL HTTPS fornecida pelo Novo Saque"
                  />
                  <p v-else class="text-xs text-n-slate-11">
                    Sandbox: https://api.novosaque.dev.br
                  </p>
                  <p class="text-xs text-n-slate-11">
                    Informe a chave do ambiente selecionado. Ao trocar de
                    ambiente, preencha a chave correspondente.
                  </p>
                  <p
                    class="rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
                  >
                    Salve a API Key e ative a integração. O acesso será
                    verificado na primeira simulação. Se houver recusa de
                    autenticação (401 ou 403), confira a chave e o ambiente.
                  </p>
                </div>
                <Input
                  v-if="requiresApiUrl"
                  v-model="settings.apiUrl"
                  :label="$t('INTEGRATION_APPS.LOCAL.API_URL')"
                  placeholder="https://api.exemplo.com.br"
                  :message="erpApiUrlError"
                  :message-type="erpApiUrlError ? 'error' : 'info'"
                  @blur="normalizeErpApiUrlField"
                />

                <p
                  v-if="settings.provider === 'bank321'"
                  class="m-0 rounded-xl bg-n-amber-3 p-3 text-sm text-n-amber-11 md:col-span-2"
                >
                  321Bank — informe a API Key, o ambiente e as credenciais de
                  acesso.
                </p>
                <Input
                  v-if="
                    requiresCredentials &&
                    !usesClientCredentials &&
                    settings.provider !== 'neon'
                  "
                  v-model="settings.username"
                  :label="
                    settings.provider === 'bank321'
                      ? 'CPF de acesso'
                      : $t('INTEGRATION_APPS.LOCAL.USERNAME')
                  "
                  autocomplete="off"
                />

                <Input
                  v-if="
                    requiresCredentials &&
                    !usesClientCredentials &&
                    settings.provider !== 'neon'
                  "
                  v-model="settings.password"
                  type="password"
                  :label="
                    settings.provider === 'bank321'
                      ? 'Senha'
                      : $t('INTEGRATION_APPS.LOCAL.PASSWORD')
                  "
                  autocomplete="new-password"
                  :show-password-toggle="isAdmin"
                />

                <Input
                  v-if="settings.provider === 'neon'"
                  v-model="settings.neonOrigin"
                  label="x-origin"
                  placeholder="Origem/canal fornecido pelo Neon"
                  autocomplete="off"
                />

                <Input
                  v-if="settings.provider === 'neon'"
                  v-model="settings.neonTokenId"
                  type="password"
                  label="token-id"
                  placeholder="Token de autenticação"
                  autocomplete="new-password"
                  :show-password-toggle="isAdmin"
                />

                <Input
                  v-if="settings.provider === 'neon'"
                  v-model="settings.neonValidationCpf"
                  label="CPF para testar a conexão"
                  placeholder="Somente 11 números"
                  inputmode="numeric"
                  maxlength="14"
                  message="Usado apenas na validação; este CPF não será salvo."
                />

                <Input
                  v-if="
                    usesClientCredentials ||
                    (props.integrationId === 'banking' &&
                      settings.provider === 'v8')
                  "
                  v-model="settings.clientId"
                  label="Client ID"
                  autocomplete="off"
                />

                <Input
                  v-if="usesClientCredentials"
                  v-model="settings.clientSecret"
                  type="password"
                  label="Client Secret"
                  autocomplete="new-password"
                  :show-password-toggle="isAdmin"
                />

                <section
                  v-if="settings.provider === 'soma'"
                  class="flex items-center justify-between gap-3 rounded-lg border border-n-weak p-3 md:col-span-2"
                >
                  <div>
                    <h4 class="text-sm font-semibold text-n-slate-12">
                      Consulta unificada
                    </h4>
                    <p class="mt-1 text-xs text-n-slate-11">
                      Ative para utilizar a consulta unificada da Soma.
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-n-slate-11">
                      {{
                        settings.somaConsultaUnificada
                          ? 'Ativada'
                          : 'Desativada'
                      }}
                    </span>
                    <Switch
                      v-model="settings.somaConsultaUnificada"
                      aria-label="Consulta unificada"
                    />
                  </div>
                </section>

                <section
                  v-if="settings.provider === 'soma'"
                  class="flex items-center justify-between gap-3 rounded-lg border border-n-weak p-3 md:col-span-2"
                >
                  <div>
                    <h4 class="text-sm font-semibold text-n-slate-12">
                      Simular sem seguro
                    </h4>
                    <p class="mt-1 text-xs text-n-slate-11">
                      Solicita simulações sem seguro. Desativado, segue o padrão
                      do banco.
                    </p>
                  </div>
                  <Switch
                    v-model="settings.somaSemSeguro"
                    aria-label="Simular sem seguro"
                  />
                </section>

                <section
                  v-if="settings.provider === 'soma'"
                  class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 md:col-span-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="i-lucide-webhook size-4 text-n-blue-10" />
                      <h4 class="text-sm font-medium text-n-slate-12">
                        {{ localCopy.somaWebhookTitle }}
                      </h4>
                    </div>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ localCopy.somaWebhookDescription }}
                    </p>
                    <p
                      v-if="hasRegisteredSomaWebhook"
                      class="mt-2 flex items-center gap-1.5 text-xs font-medium text-n-teal-10"
                    >
                      <span class="i-lucide-circle-check size-3.5" />
                      {{ localCopy.somaWebhookRegisteredAt }}
                      {{ formattedSomaWebhookRegisteredAt }}
                    </p>
                    <code
                      class="mt-2 block truncate text-xs text-n-slate-11"
                      :title="SOMA_WEBHOOK_URL"
                    >
                      {{ SOMA_WEBHOOK_URL }}
                    </code>
                  </div>
                  <Button
                    type="button"
                    class="shrink-0"
                    :label="
                      hasRegisteredSomaWebhook
                        ? localCopy.somaWebhookRegistered
                        : localCopy.somaWebhookRegister
                    "
                    :icon="
                      hasRegisteredSomaWebhook
                        ? 'i-lucide-circle-check'
                        : 'i-lucide-webhook'
                    "
                    teal
                    faded
                    :is-loading="isRegisteringSomaWebhook"
                    :disabled="
                      isRegisteringSomaWebhook ||
                      hasRegisteredSomaWebhook ||
                      !currentProviderHook?.id ||
                      !String(settings.clientId).trim() ||
                      !String(settings.clientSecret).trim()
                    "
                    @click="registerSomaWebhook"
                  />
                </section>

                <div
                  v-if="supportsAdditionalCredentialFields"
                  class="flex min-w-0 items-end gap-2"
                >
                  <Input
                    v-model="settings.typistCpf"
                    class="min-w-0 flex-1"
                    :label="additionalCredentialLabel"
                    :placeholder="additionalCredentialPlaceholder"
                  />
                  <Button
                    v-if="settings.provider === 'c6'"
                    type="button"
                    icon="i-lucide-search"
                    :label="localCopy.c6TypistCodeConsult"
                    :is-loading="isConsultingC6TypistCode"
                    :disabled="isConsultingC6TypistCode"
                    @click="consultC6TypistCode"
                  />
                </div>

                <Input
                  v-if="showLegacyMasterIndicatorField"
                  v-model="settings.masterIndicator"
                  label="Indicador Master"
                  placeholder="Ex.: 54531"
                />

                <Input
                  v-if="shouldShowApiKeyField"
                  v-model="settings.apiKey"
                  :class="{
                    'md:col-span-2': settings.provider === 'novo_saque',
                  }"
                  type="password"
                  :label="apiKeyLabel"
                  autocomplete="new-password"
                  :show-password-toggle="isAdmin"
                  :message="
                    settings.provider === 'ixcsoft'
                      ? localCopy.ixcApiKeyHint
                      : ''
                  "
                  @blur="normalizeIxcApiKeyField"
                />

                <button
                  v-if="
                    [
                      'bull',
                      'neon',
                      'fintech_corban',
                      'bank321',
                      'anchor',
                    ].includes(settings.provider)
                  "
                  type="button"
                  class="group flex w-full md:col-span-2 items-center justify-between gap-4 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :class="
                    isProductionEnvironment
                      ? 'border-n-blue-7 bg-n-blue-3/20 hover:bg-n-blue-3/30 dark:border-n-blue-8 dark:bg-n-blue-9/10 dark:ring-1 dark:ring-n-blue-8/30 dark:hover:bg-n-blue-9/15'
                      : 'border-n-weak bg-n-alpha-1 hover:border-n-strong hover:bg-n-alpha-2'
                  "
                  :aria-pressed="isProductionEnvironment"
                  @click="isProductionEnvironment = !isProductionEnvironment"
                >
                  <div class="flex min-w-0 items-center gap-3">
                    <span
                      class="grid size-9 shrink-0 place-content-center rounded-lg"
                      :class="
                        isProductionEnvironment
                          ? 'bg-n-blue-4 text-n-blue-11 dark:bg-n-blue-9/20 dark:text-n-blue-9'
                          : 'bg-n-alpha-2 text-n-slate-11'
                      "
                    >
                      <span
                        class="size-4"
                        :class="
                          isProductionEnvironment
                            ? 'i-lucide-cloud'
                            : 'i-lucide-flask-conical'
                        "
                      />
                    </span>
                    <div class="min-w-0">
                      <p class="text-sm font-medium text-n-slate-12">
                        {{
                          isProductionEnvironment ? 'Produção' : 'Homologação'
                        }}
                      </p>
                      <p
                        class="mt-0.5 text-xs text-n-slate-10 dark:text-n-slate-11"
                      >
                        Clique para usar
                        {{
                          isProductionEnvironment ? 'homologação' : 'produção'
                        }}
                      </p>
                    </div>
                  </div>
                  <span
                    class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold"
                    :class="
                      isProductionEnvironment
                        ? 'bg-n-blue-4 text-n-blue-11 dark:bg-n-blue-9/20 dark:text-n-blue-9'
                        : 'bg-n-alpha-2 text-n-slate-11'
                    "
                  >
                    <span class="size-1.5 rounded-full bg-current" />
                    {{
                      isProductionEnvironment
                        ? 'Produção ativa'
                        : 'Homologação ativa'
                    }}
                  </span>
                </button>

                <Input
                  v-if="props.integrationId === 'corban'"
                  v-model="settings.corbanHeaderName"
                  :label="localCopy.customAuthorizationHeader"
                  placeholder="Authorization"
                />
              </div>

              <div
                v-if="
                  props.integrationId === 'erp' &&
                  ['ixcsoft', 'mkauth'].includes(settings.provider)
                "
                class="grid grid-cols-1 gap-4"
                :class="
                  settings.provider === 'ixcsoft'
                    ? 'lg:grid-cols-2 xl:grid-cols-3'
                    : ''
                "
              >
                <section
                  class="flex h-full flex-col rounded-xl border border-n-weak bg-n-alpha-2 p-4 transition-colors hover:border-n-strong"
                >
                  <div class="flex h-full flex-col justify-between gap-4">
                    <div class="flex min-w-0 items-start gap-3">
                      <div
                        class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                      >
                        <span class="i-lucide-wrench size-5" />
                      </div>
                      <div class="min-w-0">
                        <h4 class="text-heading-3 text-n-slate-12">
                          {{ localCopy.customerServiceOrder }}
                        </h4>
                        <p class="text-body-small text-n-slate-11">
                          {{ localCopy.customerServiceOrderDescription }}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      :label="
                        settings.customerServiceOrder.enabled
                          ? localCopy.customerServiceOrderDeactivate
                          : localCopy.customerServiceOrderActivate
                      "
                      :icon="
                        settings.customerServiceOrder.enabled
                          ? 'i-lucide-circle-stop'
                          : 'i-lucide-wrench'
                      "
                      :color="
                        settings.customerServiceOrder.enabled ? 'teal' : 'slate'
                      "
                      :variant="
                        settings.customerServiceOrder.enabled
                          ? 'solid'
                          : 'faded'
                      "
                      class="w-full justify-center"
                      @click="
                        settings.customerServiceOrder.enabled =
                          !settings.customerServiceOrder.enabled
                      "
                    />
                  </div>
                </section>

                <section
                  v-if="settings.provider === 'ixcsoft'"
                  class="flex h-full flex-col rounded-xl border border-n-weak bg-n-alpha-2 p-4 transition-colors hover:border-n-strong"
                >
                  <div class="flex h-full flex-col justify-between gap-4">
                    <div class="flex min-w-0 items-start gap-3">
                      <div
                        class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                      >
                        <span class="i-lucide-shield-check size-5" />
                      </div>
                      <div class="min-w-0">
                        <h4 class="text-heading-3 text-n-slate-12">
                          {{ localCopy.financialTrustUnlock }}
                        </h4>
                        <p class="text-body-small text-n-slate-11">
                          {{ localCopy.financialTrustUnlockDescription }}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      :label="
                        settings.financialTrustUnlock.enabled
                          ? localCopy.financialTrustUnlockDeactivate
                          : localCopy.financialTrustUnlockActivate
                      "
                      :icon="
                        settings.financialTrustUnlock.enabled
                          ? 'i-lucide-circle-stop'
                          : 'i-lucide-shield-check'
                      "
                      :color="
                        settings.financialTrustUnlock.enabled ? 'teal' : 'slate'
                      "
                      :variant="
                        settings.financialTrustUnlock.enabled
                          ? 'solid'
                          : 'faded'
                      "
                      class="w-full justify-center"
                      @click="
                        settings.financialTrustUnlock.enabled =
                          !settings.financialTrustUnlock.enabled
                      "
                    />
                  </div>
                </section>

                <section
                  v-if="settings.provider === 'ixcsoft'"
                  class="flex h-full flex-col rounded-xl border border-n-weak bg-n-alpha-2 p-4 transition-colors hover:border-n-strong"
                >
                  <div class="flex h-full flex-col justify-between gap-4">
                    <div class="flex min-w-0 items-start gap-3">
                      <div
                        class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                      >
                        <span class="i-lucide-headphones size-5" />
                      </div>
                      <div class="min-w-0">
                        <h4 class="text-heading-3 text-n-slate-12">
                          {{ localCopy.customerServiceOpening }}
                        </h4>
                        <p class="text-body-small text-n-slate-11">
                          {{ localCopy.customerServiceOpeningDescription }}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      :label="
                        settings.customerServiceOpening.enabled
                          ? localCopy.customerServiceOpeningDeactivate
                          : localCopy.customerServiceOpeningActivate
                      "
                      :icon="
                        settings.customerServiceOpening.enabled
                          ? 'i-lucide-circle-stop'
                          : 'i-lucide-headphones'
                      "
                      :color="
                        settings.customerServiceOpening.enabled
                          ? 'teal'
                          : 'slate'
                      "
                      :variant="
                        settings.customerServiceOpening.enabled
                          ? 'solid'
                          : 'faded'
                      "
                      class="w-full justify-center"
                      @click="
                        settings.customerServiceOpening.enabled =
                          !settings.customerServiceOpening.enabled
                      "
                    />
                  </div>
                </section>

                <section
                  v-if="settings.provider === 'ixcsoft'"
                  class="rounded-xl border border-n-weak bg-n-alpha-2 p-4 transition-colors hover:border-n-strong lg:col-span-2 xl:col-span-3"
                  :class="
                    settings.invoiceNotifications.enabled
                      ? 'space-y-4'
                      : 'flex h-full flex-col'
                  "
                >
                  <div
                    class="flex gap-4"
                    :class="
                      settings.invoiceNotifications.enabled
                        ? 'flex-col sm:flex-row sm:items-center sm:justify-between'
                        : 'h-full flex-col justify-between'
                    "
                  >
                    <div class="flex min-w-0 items-start gap-3">
                      <div
                        class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                      >
                        <span class="i-lucide-receipt-text size-5" />
                      </div>
                      <div class="min-w-0">
                        <h4 class="text-heading-3 text-n-slate-12">
                          {{ localCopy.invoiceNotifications }}
                        </h4>
                        <p class="text-body-small text-n-slate-11">
                          {{ localCopy.invoiceNotificationsDescription }}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      :label="
                        settings.invoiceNotifications.enabled
                          ? 'Desativar avisos automáticos'
                          : localCopy.invoiceNotificationsEnabled
                      "
                      :icon="
                        settings.invoiceNotifications.enabled
                          ? 'i-lucide-bell-ring'
                          : 'i-lucide-bell'
                      "
                      :color="
                        settings.invoiceNotifications.enabled ? 'teal' : 'slate'
                      "
                      :variant="
                        settings.invoiceNotifications.enabled
                          ? 'solid'
                          : 'faded'
                      "
                      class="shrink-0 justify-center"
                      :class="
                        settings.invoiceNotifications.enabled
                          ? 'w-full sm:w-auto'
                          : 'w-full'
                      "
                      @click="
                        settings.invoiceNotifications.enabled =
                          !settings.invoiceNotifications.enabled
                      "
                    />
                  </div>

                  <div
                    v-if="settings.invoiceNotifications.enabled"
                    class="space-y-4"
                  >
                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Input
                        v-model="settings.invoiceNotifications.start_time"
                        type="time"
                        :label="localCopy.invoiceStartTime"
                      />
                      <Input
                        v-model.number="
                          settings.invoiceNotifications.message_interval_seconds
                        "
                        type="number"
                        min="0"
                        :label="localCopy.invoiceInterval"
                      />
                    </div>

                    <div class="space-y-2">
                      <div>
                        <p class="text-sm font-medium text-n-slate-12">
                          {{ localCopy.invoiceDueDays }}
                        </p>
                        <p class="text-xs text-n-slate-11">
                          {{ localCopy.invoiceDueDaysHelp }}
                        </p>
                      </div>
                      <div
                        class="grid grid-cols-7 gap-2 sm:grid-cols-10 md:grid-cols-[repeat(16,minmax(0,1fr))]"
                      >
                        <button
                          v-for="day in 31"
                          :key="day"
                          type="button"
                          class="flex h-9 min-w-9 items-center justify-center rounded-md border text-sm font-medium transition-colors"
                          :class="
                            settings.invoiceNotifications.due_days.includes(day)
                              ? 'border-n-blue-9 bg-n-blue-9 text-white'
                              : 'border-n-weak bg-n-alpha-2 text-n-slate-12 hover:border-n-blue-8 hover:text-n-blue-10'
                          "
                          @click="toggleInvoiceDueDay(day)"
                        >
                          {{ day }}
                        </button>
                      </div>
                    </div>

                    <section
                      v-for="ruleKey in ['before_due', 'after_due']"
                      :key="ruleKey"
                      class="rounded-lg border border-n-weak bg-n-card p-4"
                    >
                      <div class="mb-4 flex items-center justify-between gap-4">
                        <h5 class="text-heading-3 text-n-slate-12">
                          {{
                            ruleKey === 'before_due'
                              ? localCopy.invoiceBeforeDue
                              : localCopy.invoiceAfterDue
                          }}
                        </h5>
                        <Switch
                          v-model="
                            settings.invoiceNotifications[ruleKey].enabled
                          "
                        />
                      </div>

                      <div
                        v-if="settings.invoiceNotifications[ruleKey].enabled"
                        class="grid grid-cols-1 gap-3 md:grid-cols-2"
                      >
                        <Input
                          v-model.number="
                            settings.invoiceNotifications[ruleKey].days
                          "
                          type="number"
                          min="0"
                          :label="
                            ruleKey === 'before_due'
                              ? localCopy.invoiceDaysBefore
                              : localCopy.invoiceDaysAfter
                          "
                        />

                        <label
                          class="flex flex-col gap-2 text-sm text-n-slate-12"
                        >
                          <span>{{ localCopy.invoiceInbox }}</span>
                          <ComboBox
                            v-model="
                              settings.invoiceNotifications[ruleKey].inbox_id
                            "
                            :options="invoiceInboxOptions"
                            :placeholder="localCopy.invoiceInboxPlaceholder"
                            @update:model-value="
                              resetInvoiceTemplateForInbox(ruleKey)
                            "
                          />
                        </label>

                        <label
                          class="flex flex-col gap-2 text-sm text-n-slate-12 md:col-span-2"
                        >
                          <span>{{ localCopy.invoiceTemplate }}</span>
                          <ComboBox
                            v-model="
                              settings.invoiceNotifications[ruleKey]
                                .template_name
                            "
                            :options="
                              invoiceTemplateOptions(
                                settings.invoiceNotifications[ruleKey].inbox_id
                              )
                            "
                            :placeholder="localCopy.invoiceTemplatePlaceholder"
                            @update:model-value="
                              syncInvoiceTemplateLanguage(ruleKey)
                            "
                          />
                          <span
                            v-if="
                              settings.invoiceNotifications[ruleKey].inbox_id &&
                              !templateOptionsForInbox(
                                settings.invoiceNotifications[ruleKey].inbox_id
                              ).length
                            "
                            class="text-xs text-n-slate-11"
                          >
                            {{ localCopy.invoiceNoTemplates }}
                          </span>
                        </label>
                      </div>
                    </section>
                  </div>
                </section>
              </div>

              <section
                v-if="
                  props.integrationId === 'corban' && showCorbanAdvancedFields
                "
                class="space-y-4 rounded-lg border border-n-weak bg-n-alpha-2 p-4"
              >
                <div>
                  <h4 class="text-heading-3 text-n-slate-12">
                    {{ localCopy.requestHeaders }}
                  </h4>
                  <p class="text-body-small text-n-slate-11">
                    {{ localCopy.requestHeadersHelp }}
                  </p>
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(header, index) in settings.headers"
                    :key="`header-${index}`"
                    class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_44px] md:gap-0"
                  >
                    <Input
                      v-model="header.key"
                      :label="
                        index === 0 ? localCopy.customAuthorizationHeader : ''
                      "
                      placeholder="Authorization"
                      custom-input-class="md:!rounded-r-none md:!border-r-0"
                    />

                    <Input
                      v-model="header.value"
                      type="password"
                      :label="
                        index === 0 ? localCopy.customAuthorizationValue : ''
                      "
                      placeholder="Valor do header"
                      custom-input-class="md:!rounded-l-none"
                      :show-password-toggle="isAdmin"
                    />

                    <button
                      type="button"
                      class="h-10 self-end flex items-center justify-center rounded-lg border border-n-weak text-n-slate-12 hover:border-n-ruby-8 hover:text-n-ruby-10"
                      :disabled="settings.headers.length === 1"
                      :class="
                        settings.headers.length === 1
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      "
                      :title="localCopy.removeHeader"
                      :aria-label="localCopy.removeHeader"
                      @click="removeCorbanHeader(index)"
                    >
                      <span class="i-lucide-trash-2 size-4" />
                    </button>
                  </div>

                  <button
                    type="button"
                    class="h-10 rounded-lg border border-n-weak px-3 text-sm text-n-slate-12 hover:border-n-blue-8 hover:text-n-blue-10"
                    @click="addCorbanHeader"
                  >
                    {{ localCopy.addHeaderLabel }}
                  </button>
                </div>
              </section>
            </div>

            <div
              v-if="
                props.integrationId === 'corban' && showCorbanAdvancedFields
              "
              class="space-y-4 rounded-lg border border-n-weak bg-n-alpha-2 p-4"
            >
              <div>
                <h4 class="text-heading-3 text-n-slate-12">
                  {{ localCopy.products }}
                </h4>
                <p class="text-body-small text-n-slate-11">
                  {{ localCopy.fieldMappingHelp }}
                </p>
              </div>

              <div class="space-y-4">
                <section
                  v-for="product in CORBAN_PRODUCT_OPTIONS"
                  :key="product.value"
                  class="rounded-lg border border-n-weak bg-n-card p-4"
                >
                  <div class="flex items-center justify-between gap-4">
                    <div>
                      <h5 class="text-heading-3 text-n-slate-12">
                        {{ product.label }}
                      </h5>
                      <p class="text-body-small text-n-slate-11">
                        {{ localCopy.productUrl }}
                      </p>
                    </div>
                    <Switch
                      v-model="settings.products[product.value].enabled"
                    />
                  </div>

                  <div
                    v-if="settings.products[product.value].enabled"
                    class="mt-4 space-y-4"
                  >
                    <div
                      class="grid grid-cols-1 gap-3 md:grid-cols-[120px_1fr]"
                    >
                      <label
                        class="flex flex-col gap-2 text-sm text-n-slate-12"
                      >
                        <span>{{ localCopy.requestMethod }}</span>
                        <ComboBox
                          v-model="settings.products[product.value].method"
                          :options="CORBAN_HTTP_METHOD_OPTIONS"
                        />
                      </label>

                      <Input
                        v-model="settings.products[product.value].url"
                        :label="localCopy.productUrl"
                        placeholder="https://api.exemplo.com.br/consulta"
                      />
                    </div>

                    <TextArea
                      v-if="settings.products[product.value].method === 'POST'"
                      v-model="settings.products[product.value].body"
                      :label="localCopy.requestBody"
                      :message="localCopy.requestBodyHelp"
                      :placeholder="localCopy.requestBodyPlaceholder"
                      auto-height
                      min-height="6rem"
                      max-height="16rem"
                    />

                    <div>
                      <p class="mb-2 text-sm font-medium text-n-slate-12">
                        {{ localCopy.fieldMapping }}
                      </p>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input
                          v-for="field in CORBAN_MAPPING_FIELDS"
                          :key="field.key"
                          v-model="
                            settings.products[product.value].mapping[field.key]
                          "
                          :label="field.label"
                          :placeholder="field.placeholder"
                        />
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          <section
            v-if="usesProviderDialog"
            class="rounded-xl border p-4"
            :class="providerStatusClass"
          >
            <div class="flex items-center justify-between gap-4">
              <div class="flex min-w-0 items-center gap-3">
                <span
                  class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                >
                  <span
                    :class="
                      settings.enabled
                        ? 'i-lucide-circle-check text-n-teal-10'
                        : 'i-lucide-circle-pause'
                    "
                    class="size-5"
                  />
                </span>
                <div class="min-w-0">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    Status da integração
                  </h4>
                  <p class="mt-0.5 text-xs text-n-slate-10">
                    {{
                      settings.enabled
                        ? 'A integração está ativa e disponível para uso.'
                        : 'A integração está desativada e não será utilizada.'
                    }}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                :label="
                  settings.enabled
                    ? 'Desativar integração'
                    : 'Ativar integração'
                "
                :icon="
                  settings.enabled
                    ? 'i-lucide-circle-pause'
                    : 'i-lucide-circle-play'
                "
                :teal="!settings.enabled"
                :slate="settings.enabled"
                faded
                class="shrink-0"
                @click="toggleProviderStatus"
              />
            </div>
          </section>
        </div>

        <template #footer>
          <footer
            class="sticky -bottom-6 z-20 -mx-6 -mb-6 flex flex-col-reverse gap-3 border-t border-n-weak bg-n-solid-2/95 px-6 py-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between"
          >
            <Button
              v-if="currentProviderHook?.id"
              :label="localCopy.delete"
              icon="i-lucide-trash-2"
              ruby
              faded
              :is-loading="uiFlags.isDeletingHook"
              :disabled="uiFlags.isDeletingHook || uiFlags.isCreatingHook"
              @click="deleteSettings"
            />
            <span v-else />

            <div class="flex flex-wrap justify-end gap-2">
              <Button
                v-if="
                  ['banking', 'corban'].includes(props.integrationId) &&
                  settings.provider !== 'novo_saque'
                "
                :label="
                  props.integrationId === 'banking' &&
                  hasValidatedBankConnection
                    ? 'Testar novamente'
                    : $t('INTEGRATION_APPS.LOCAL.VALIDATE_CONNECTION')
                "
                icon="i-lucide-check-circle"
                teal
                faded
                :is-loading="isValidatingConnection"
                :disabled="!isValidationFormValid || isValidatingConnection"
                @click="validateConnection"
              />
              <Button
                :label="$t('INTEGRATION_APPS.LOCAL.CANCEL')"
                slate
                faded
                @click="providerDialogRef.close()"
              />
              <Button
                label="Salvar configuração"
                icon="i-lucide-save"
                solid
                blue
                :is-loading="uiFlags.isCreatingHook"
                :disabled="!isFormValid || uiFlags.isCreatingHook"
                @click="saveSettings"
              />
            </div>
          </footer>
        </template>
      </Dialog>

      <Dialog
        ref="mappingReviewDialogRef"
        width="xl"
        :title="localCopy.mappingReviewTitle"
        :show-cancel-button="false"
        :show-confirm-button="false"
      >
        <div class="space-y-5">
          <p class="text-body-small text-n-slate-11">
            {{ localCopy.mappingReviewDescription }}
          </p>

          <div
            v-if="!Object.keys(mappingReview.products).length"
            class="rounded-lg border border-dashed border-n-weak p-6 text-center text-sm text-n-slate-11"
          >
            {{ localCopy.mappingNotDetected }}
          </div>

          <section
            v-for="(productReview, productKey) in mappingReview.products"
            :key="productKey"
            class="rounded-lg border border-n-weak bg-n-alpha-2 p-4"
          >
            <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 class="text-heading-3 text-n-slate-12">
                  {{ productLabel(productKey) }}
                </h4>
                <p class="text-body-small text-n-slate-11">
                  {{
                    productReview.ok
                      ? localCopy.mappingDetected
                      : productReview.error || localCopy.mappingNotDetected
                  }}
                </p>
              </div>
              <span
                v-if="productReview.http_status"
                class="rounded-md bg-n-solid-3 px-2 py-1 text-xs text-n-slate-11"
              >
                {{ localCopy.httpStatus }} {{ productReview.http_status }}
              </span>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                v-for="field in CORBAN_MAPPING_FIELDS"
                :key="field.key"
                v-model="productReview.mapping[field.key]"
                :label="field.label"
                :placeholder="field.placeholder"
              />
            </div>

            <details
              v-if="productReview.paths?.length"
              class="mt-4 rounded-lg border border-n-weak bg-n-card p-3"
            >
              <summary
                class="cursor-pointer text-sm font-medium text-n-slate-12"
              >
                {{ localCopy.detectedPaths }}
              </summary>
              <div class="mt-3 flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                <button
                  v-for="path in productReview.paths"
                  :key="path"
                  type="button"
                  class="rounded-md border border-n-weak bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-3"
                  @click="navigator?.clipboard?.writeText(path)"
                >
                  {{ path }}
                </button>
              </div>
            </details>
          </section>

          <div class="flex flex-wrap justify-end gap-3">
            <Button
              :label="localCopy.closeMapping"
              slate
              faded
              @click="mappingReviewDialogRef.close()"
            />
            <Button
              :label="localCopy.applyMapping"
              icon="i-lucide-arrow-left-right"
              solid
              blue
              @click="applyReviewedMapping"
            />
          </div>
        </div>
      </Dialog>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.bank-order-move {
  transition: transform 180ms ease;
}
</style>
